/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 * 
 * UPDATED: Added self-reformulation option for teams with flagged messages.
 */


import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Collapse,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
  Chat as ChatIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  Update as UpdateIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useDebate } from '../contexts/DebateContext';
import { Message, ShitmoveFlag, Team } from '../types';
import { exportDebateAsJson, updateDebateStatus, getDebateById, getUserNotes, saveUserNotes } from '../services/debateService';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';
import TeamInfo from '../components/TeamInfo';
import DebateSummary from '../components/DebateSummary';
import { useAuth } from '../contexts/AuthContext';
// Add import for team utilities
import { getTeamName, getUserName } from '../utils/teamUtils';
// Add import for the user service
import { getUsersByIds } from '../services/userService';
// Add import for PusherDebateChat component
import PusherDebateChat from '../components/PusherDebateChat';
import DebateMessageInputForm from '../components/DebateMessageInputForm';
import TypingIndicator from '../components/TypingIndicator';
import usePusherForDebateRoom from '../hooks/usePusherForDebateRoom';
// Import usePusherConnection
import { usePusherConnection } from '../hooks/usePusher';
// Import translation components
import { formatMessageContent } from '../utils/messageFormatter';
import logger from '../utils/logger';

// COMPLETED: All useAsyncTranslation calls have been converted to t() calls!

// Add a controlled logging function at the top of the component
const debugLog = (message: string, data?: any) => {
  // DISABLE ALL LOGGING
  return; // No logging at all

  // The code below will never run, but leaving commented for future reference
  /*
  // Even in development, only log important messages to reduce spam
  // Don't log repetitive messages like render calls, team checks, etc.
  const isRepetitiveMessage = 
    message.includes("Checking if it is current team's turn") ||
    message.includes("Rendering message") ||
    message.includes("Team:") ||
    message.includes("Unlifted flags count");
  
  // Skip repetitive messages
  if (isRepetitiveMessage) {
    return;
  }
  
  // Log important messages with data
  if (data) {
    logger.info(`DebateRoomPage: ${message}`, data);
  } else {
    logger.info(`DebateRoomPage: ${message}`);
  }
  */
};

// Override console methods for this component to prevent excessive logging
const originalConsoleLog = console.log;
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// Replace console.log with no-op for this file
console.log = function(...args: any[]) {
  // Suppress all console logs from this file
  if (args[0] && typeof args[0] === 'string' && args[0].includes('DebateRoomPage:')) {
    return;
  }
  // Pass through other logs
  originalConsoleLog.apply(console, args);
};

// Replace console.error with no-op for this file
console.error = function(...args: any[]) {
  // Suppress all console errors from this file
  if (args[0] && typeof args[0] === 'string' && args[0].includes('DebateRoomPage:')) {
    return;
  }
  // Pass through other errors
  originalConsoleError.apply(console, args);
};

// Replace console.warn with no-op for this file
console.warn = function(...args: any[]) {
  // Suppress all console warnings from this file
  if (args[0] && typeof args[0] === 'string' && args[0].includes('DebateRoomPage:')) {
    return;
  }
  // Pass through other warnings
  originalConsoleWarn.apply(console, args);
};

// Add this interface to define the pusher return type - place it before the DebateRoomPage component
interface PusherForDebateRoomReturn {
  isConnected: boolean;
  debateChannel: any;
  userChannel: any;
  typingUsers: Array<{
    userId: string;
    username?: string;
    teamId: string;
    timestamp: Date;
  }>;
  sendTypingStatus: (isTyping: boolean) => Promise<void>;
}

const DebateRoomPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentDebate, fetchDebateById, sendMessage, liftFlag, socket, currentTeamWithFlag, setCurrentTeamWithFlag } = useDebate();
  const { user, isAuthenticated, redirectAfterLogin } = useAuth();
  const { t } = useTranslation();
  
  // Translation hooks for all UI text (Batch 1: converted to t() calls)
  // Basic UI labels - converted to use proper translation keys
  // BATCH 2: jestersGauntletCountsText, aboutPenaltiesText -> converted to t() calls
  // BATCH 6: aboutPenaltiesDescText -> converted to t() calls
  // BATCH 2: noMessagesStartDebateText, waitingForFirstTeamText, reformulateMessageText -> converted to t() calls
  // BATCH 4: youChosenReformulateText, otherTeamRequestedReformulateText -> converted to t() calls
  // BATCH 5: chooseReformulateNowText, penaltyFlagsNoteText, teamsLoseTooltipText -> converted to t() calls
  
  // Toast and dynamic message translations
  // BATCH 5: realTimeConnectionText, otherTeamChosenReformulateToastText, otherTeamRequestedReformulateToastText, youChosenReformulateToastText -> converted to t() calls
  // BATCH 3: allFlagsLiftedText, failedLiftFlagsText, reformulationRequestSentText, failedSendReformulationText, youConcededText, failedConcedeText -> converted to t() calls
  // BATCH 4: failedExportText, copyDebateLinkText, linkCopiedText, exportDebateText -> converted to t() calls
  
  // Buttons and actions
  // BATCH 2: liftAllFlagsText, requestReformulationText, sendMessageText, reformulateText -> converted to t() calls
  // BATCH 4: Note - cancelText, yesText, noText already exist in i18n.ts, no need for new keys
  
  // Team and user status (converted to t() calls)
  // BATCH 2: isTypingText, areTypingText -> converted to t() calls
  // BATCH 4: andText, othersText -> converted to t() calls
  
  // Error and loading states
  // BATCH 2: loadingDebateText, debateNotFoundText, errorLoadingDebateText -> converted to t() calls
  // BATCH 4: retryText, pleaseLoginText, loginText -> converted to t() calls
  
  // Dialog titles and content
  // BATCH 2: concedeDefeatText -> converted to t() calls
  // BATCH 5: areYouSureConcededText, summaryGeneratedText -> converted to t() calls
  // BATCH 4: Note - goToDebateRoomText already exists in i18n.ts, using existing key
  
  // BATCH 5: jestersGauntletDetectedText, reformulationNeededText, thisMessageContainsText, flagTypeText, optionsText, messageFlagsText -> converted to t() calls
  
  // BATCH 5: failedInitiateReformulationText, errorLiftingFlagMissingIdText, issueUpdatingDebateStatusText, couldNotIdentifyOpposingTeamText, failedEndDebateAfterConcessionText -> converted to t() calls
  // BATCH 6: onlyFirstTeamCanStartText, debateHasConcludedText, returnToHomeText -> Note: debateHasConcludedText and returnToHomeText already exist, using existing keys
  // BATCH 3: analyzingText, recommendationText, byText -> converted to t() calls
  // BATCH 6: youRequestedReformulationText, otherTeamChosenReformulateText, actionRequiredText, otherTeamMessageFlaggedText, reviewFlagsText, selfInitiatedReformulationText, reformulationRequestedText, yourMessageFlaggedAgainText, waitOtherTeamReviewText, chooseReformulateAgainText, yourRecentMessageFlaggedText, reformulationRequestedWaitText, yourMessageFlaggedNeedsReformulationText, submitImprovedMessageText, waitingOtherTeamSendText, waitingThemSubmitImprovedText -> converted to t() calls
  
  // Restore original console methods on unmount
  useEffect(() => {
    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);
  
  // Local state
  // messageInput is now handled by DebateMessageInputForm
  
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<ShitmoveFlag | null>(null);
  const [, setExportLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [errorMessage, setError] = useState('');
  const [, setShowFlagBanner] = useState(false);
  const [isReformulating, setIsReformulating] = useState(false);
  const [currentTurn, setCurrentTurn] = useState<string | null>(null);
  const [flagActionTaken, setFlagActionTaken] = useState(false);
  const [reformulationRequested, setReformulationRequested] = useState(false);
  const [processedMessageIds, setProcessedMessageIds] = useState<Set<string>>(new Set());
  const [selfInitiatedReformulationTeamId, setSelfInitiatedReformulationTeamId] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [notesSaving, setNotesSaving] = useState<boolean>(false);
  
  // Track messages that have had reformulation requested
  const [reformulationRequestedMessages, setReformulationRequestedMessages] = useState<Set<string>>(new Set());
  
  // Add this state variable after the other state declarations
  const [reformulationMessage] = useState('');
  // Track lifted-flag notes per message id
  const [liftedNotes, setLiftedNotes] = useState<Record<string, { by: string; at: string; flags: any[]; expanded: boolean }>>({});
  // Per-user notification opt-in within debate room
  const [notifyOnOpponentMessage, setNotifyOnOpponentMessage] = useState<boolean>(false);

  // Load and persist per-debate notification preference
  useEffect(() => {
    // On debate load, attempt to infer from server by checking user-specific map via a lightweight call
    // We avoid a new API; we cache in localStorage per debate for now and rely on PUT on change
    try {
      if (!id) return;
      const key = `notifyOpp-${id}`;
      const v = localStorage.getItem(key);
      if (v !== null) setNotifyOnOpponentMessage(v === '1');
    } catch {}
  }, [id]);

  useEffect(() => {
    // Persist toggle to server when changed
    if (!id) return;
    try { localStorage.setItem(`notifyOpp-${id}`, notifyOnOpponentMessage ? '1' : '0'); } catch {}
    // Fire-and-forget server update
    (async () => {
      try {
        await api.put(`/debates/${id}/notifications/opponent-message`, { notify: notifyOnOpponentMessage });
      } catch (e) {
        // no-op; keep local state
      }
    })();
  }, [id, notifyOnOpponentMessage]);

  // Load personal notes when debate changes
  useEffect(() => {
    const loadNotes = async () => {
      try {
        if (currentDebate?.id) {
          const text = await getUserNotes(currentDebate.id);
          setNotes(text || '');
        } else {
          setNotes('');
        }
      } catch (e) {
        logger.warn('Failed to load notes:', e);
      }
    };
    loadNotes();
  }, [currentDebate?.id]);
  
  // EMERGENCY FIX: Completely disable ALL automatic flag dialog opening
  useEffect(() => {
    // This prevents infinite flag dialogs - only manual opening is allowed
    const handleMessage = () => {
      // Banner removed per user request - don't set showFlagBanner at all
      if (currentDebate?.messages?.length && selectedTeam) {
        const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
        void (lastMessage.flags?.some(flag => !flag.lifted) || false);
        void (String(lastMessage.teamId) === String(selectedTeam));
        // No longer show any flag banner
      }
    };
    
    // Initial check
    handleMessage();
    
    return () => {
      // Clean up flag states on unmount
      setFlagDialogOpen(false);
      setSelectedFlag(null);
    };
  }, [currentDebate?.messages, selectedTeam]);
  
  // Concede dialog state
  const [concedeDialogOpen, setConcedeDialogOpen] = useState(false);
  const [debateResultDialogOpen, setDebateResultDialogOpen] = useState(false);
  
  // Add shitmove counting state
  const [shitmoveCounts, setShitmoveCounts] = useState<{
    [teamId: string]: {
      total: number;
      byType: { [type: string]: number };
    };
  }>({});
  
  // Add state for debate over dialog
  const [showDebateOverDialog, setShowDebateOverDialog] = useState(false);
  
  // CRITICAL FIX: Add state to prevent multiple debate ending calls
  const [isEndingDebate, setIsEndingDebate] = useState(false);
  
  // Refs
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const prevMessageCountRef = React.useRef<number>(0);
  // When the latest AI response is edited in-place (same message id),
  // the message count may not change, so we also track last content length.
  const prevLastMessageContentLengthRef = React.useRef<number>(0);
  // wasReformulating ref removed - now handled in DebateMessageInputForm
  const loadedDebateRef = React.useRef<boolean>(false);
  
  // Track which message is showing flag details
  const [activeFlagMessage, setActiveFlagMessage] = useState<string | null>(null);

  // Reserved for future scroll-to-top UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setHasScrolledToTop] = useState(false);

  // Add state for storing usernames
  const [usernames, setUsernames] = useState<{[key: string]: string}>({});
  
  // Replace usePusher toggle with a hidden constant
  // Use true to enable Pusher by default, but don't display UI toggle
  const usePusher = true; // Always use Pusher, but hide it in the UI
  
  // Add team colors for styling typing indicators 
  const [teamColors, setTeamColors] = useState<{[key: string]: string}>({});
  const [aiDefenseFailed, setAiDefenseFailed] = useState(false);
  const [aiFailureMessage, setAiFailureMessage] = useState<string | null>(null);
    
  // Initialize Pusher for debate room
  const pusher = usePusherForDebateRoom(id || '', selectedTeam || '') as PusherForDebateRoomReturn;
    
  // Inside the DebateRoomPage component, add this after the other useStates
  const isPusherConnected = usePusherConnection();
  
  // Add after the usePusherForDebateRoom initialization
  // Debug log Pusher connection status
  useEffect(() => {
    // Merge any transient lifted note cached by Pusher handler
    try {
      if (window && (window as any).__liftedFlagNotes) {
        const notes = (window as any).__liftedFlagNotes as Record<string, any>;
        if (notes && Object.keys(notes).length > 0) {
          setLiftedNotes(prev => {
            const merged = { ...prev };
            for (const [k, v] of Object.entries(notes)) {
              if (!merged[k]) {
                merged[k] = { by: String((v as any).by), at: String((v as any).at), flags: (v as any).flags || [], expanded: false };
              }
            }
            return merged;
          });
        }
      }
    } catch {}
  }, [currentDebate?.messages]);

  // Debug/log Pusher connection changes and show toast when connected
  useEffect(() => {
    logger.info(`Pusher connection status: ${isPusherConnected ? 'CONNECTED' : 'DISCONNECTED'}`);
    if (isPusherConnected) {
      toast.success(t('realTimeConnectionText'));
    }
  }, [isPusherConnected, t]);

  // *** ADD THIS NEW useEffect for Pusher 'reformulation_request' events ***
  useEffect(() => {
    if (!pusher || !pusher.debateChannel) return;

    const handlePusherReformulationRequest = (data: any) => {
      if (!data || !data.teamId) {
        logger.warn('DebateRoomPage: Received incomplete reformulation_request data via Pusher', data);
        return;
      }
      
      const newTeamId = data.teamId;
      const isSelfInitiated = data.selfInitiated === true;
      
      logger.info(`DebateRoomPage: Received reformulation_request via PUSHER:`, data);

      // Keep teammates in sync: clicker already updated this Set locally; others only see it via Pusher
      if (data.messageId) {
        setReformulationRequestedMessages(prev => {
          const next = new Set(Array.from(prev));
          next.add(String(data.messageId));
          return next;
        });
      }
      
      // CRITICAL FIX: Handle self-initiated reformulation differently
      if (isSelfInitiated) {
        // For self-initiated reformulation, always update the selfInitiatedReformulationTeamId
        // regardless of current state to ensure UI consistency
        logger.info(`DebateRoomPage: Pusher self-initiated reformulation for team ${newTeamId}`);
        
        // Only update currentTeamWithFlag if it's not already set for this team
        if (currentTeamWithFlag !== newTeamId) {
        setCurrentTeamWithFlag(newTeamId);
        }
        
        // Always update the self-initiated flag
        setSelfInitiatedReformulationTeamId(newTeamId);
        
        // FIXED: Show appropriate notifications based on which team you are
        if (newTeamId === selectedTeam) {
          // This is the team that initiated reformulation - they already got a notification
          logger.info('DebateRoomPage: Self-reformulation initiated by current team - no notification needed');
          } else {
          // This is the OTHER team - show them that the opposing team chose to reformulate
          toast.success(t('otherTeamChosenReformulateToastText'));
        }
      } else {
        // For opponent-requested reformulation, use the original logic
        if (newTeamId !== currentTeamWithFlag) {
          logger.info(`DebateRoomPage: Pusher opponent-requested reformulation from ${currentTeamWithFlag} to ${newTeamId}`);
          setCurrentTeamWithFlag(newTeamId);
          
          // Show appropriate notifications
          if (newTeamId === selectedTeam) {
            toast.error(t('otherTeamRequestedReformulateToastText'));
          }
          // No notification for the requesting team - handled elsewhere
        } else {
          logger.info('DebateRoomPage: Pusher opponent-requested reformulation matches currentTeamWithFlag; still syncing shared UI');
        }

        // Requesting team (including teammates who didn't click): hide Action Required
        if (selectedTeam && String(selectedTeam) !== String(newTeamId)) {
          setFlagActionTaken(true);
        }
      }

      // Refresh debate so turn + reformulationRequest match server for all clients
      if (id && typeof fetchDebateById === 'function') {
        fetchDebateById(id, true).catch(err => {
          logger.error('DebateRoomPage: Error refreshing debate after reformulation_request:', err);
        });
      }
    };
    
    logger.info('DebateRoomPage: Binding Pusher listener for reformulation_request event.');
    pusher.debateChannel.bind('reformulation_request', handlePusherReformulationRequest);
    
    return () => {
      if (pusher && pusher.debateChannel) {
        logger.info('DebateRoomPage: Unbinding Pusher listener for reformulation_request event.');
        pusher.debateChannel.unbind('reformulation_request', handlePusherReformulationRequest);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pusher/t in handlers, omit to avoid re-bind
  }, [pusher?.debateChannel, currentTeamWithFlag, selectedTeam, setCurrentTeamWithFlag, setSelfInitiatedReformulationTeamId, id, fetchDebateById]);
  
  // *** AI Defense Failure Event Handlers ***
  useEffect(() => {
    if (!pusher || !pusher.debateChannel) return;

    const handleCubeAIFailed = (data: any) => {
      logger.info('DebateRoomPage: Received cube_ai_failed event:', data);
      setAiDefenseFailed(true);
      setAiFailureMessage(data.message || t('cubeAIDefenseFailedDescription'));
      
      // Show toast notification
      toast.error(t('cubeAIDefenseFailedText'));
    };

    const handleCubeDefenseFailed = (data: any) => {
      logger.info('DebateRoomPage: Received cube_defense_failed event:', data);
      setAiDefenseFailed(true);
      setAiFailureMessage(data.message || t('cubeAIDefenseFailedDescription'));
      
      // Show toast notification
      toast.error(t('cubeAIDefenseFailedText'));
    };

    logger.info('DebateRoomPage: Binding Pusher listeners for AI failure events.');
    pusher.debateChannel.bind('cube_ai_failed', handleCubeAIFailed);
    pusher.debateChannel.bind('cube_defense_failed', handleCubeDefenseFailed);
    
    return () => {
      if (pusher && pusher.debateChannel) {
        logger.info('DebateRoomPage: Unbinding Pusher listeners for AI failure events.');
        pusher.debateChannel.unbind('cube_ai_failed', handleCubeAIFailed);
        pusher.debateChannel.unbind('cube_defense_failed', handleCubeDefenseFailed);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pusher/t stable
  }, [pusher?.debateChannel, t]);
  
  // *** FIXED ***: Helper to check if the current team needs to handle flags
  const mustHandleFlags = useMemo(() => {
    if (!currentDebate?.messages?.length || !selectedTeam) return false;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    if (!lastMessage || (!(lastMessage as any).id && !(lastMessage as any)._id)) return false;
    
    // If we've already processed this message, don't handle flags again
    if (processedMessageIds.has(String((lastMessage as any).id || (lastMessage as any)._id))) {
      return false;
    }
    
    const hasUnliftedFlags = lastMessage.flags?.some(flag => !flag.lifted) || false;
    if (!hasUnliftedFlags) return false;
    
    // Check if it's my message or the other team's message
    const isMessageOwner = String(lastMessage.teamId) === String(selectedTeam);
    
    // If it's my message with flags, I need to handle it by reformulating
    if (isMessageOwner) {
      return true;
    }
    
    // If it's the other team's message with flags, I need to handle it by lifting or requesting reformulation
    // This prevents the text input from showing when I should be handling flags instead
    return !isMessageOwner;
  }, [currentDebate?.messages, selectedTeam, processedMessageIds]);

  // Add handlers for flag actions at the top level
  const handleLiftAllFlags = useCallback(async (message: Message) => {
    if (!currentDebate) return;
    
    try {
      // Mark that action has been taken
      setFlagActionTaken(true);

      // Lift all unlifted flags
      const unliftedFlags = (message.flags || []).filter(flag => !flag.lifted);
      logger.info('DebateRoomPage: Lifting all flags:', unliftedFlags);
      
      for (const flag of unliftedFlags) {
        // Get flag ID and fall back to using _id if id is not available
        const flagId = flag.id || flag._id;
        if (!flagId) {
          logger.error('Flag has no ID:', flag);
          continue;
        }
        
        try {
          await liftFlag(currentDebate.id, (message as any).id || (message as any)._id, flagId);
          logger.info('DebateRoomPage: Successfully lifted flag:', flagId);
        } catch (error) {
          logger.error('DebateRoomPage: Error lifting individual flag:', error);
          // Continue with other flags even if one fails
          continue;
        }
      }
      
      // Show success message
      toast.success(t('allFlagsLiftedText'));
      // Record a collapsible note under this message for later review
      setLiftedNotes(prev => ({
        ...prev,
        [String((message as any).id || (message as any)._id)]: {
          by: usernames[String(user?.id)] || user?.username || 'You',
          at: new Date().toISOString(),
          flags: unliftedFlags,
          expanded: false
        }
      }));
    } catch (error) {
      logger.error('DebateRoomPage: Error in handleLiftAllFlags:', error);
      toast.error(t('failedLiftFlagsText'));
      // Reset flag action taken state if there was an error
      setFlagActionTaken(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t, usernames, user in closure intentionally
  }, [currentDebate, liftFlag]);

  const handleRequestReformulation = useCallback(async (message: Message) => {
    if (!currentDebate) return;
    
    // Mark that action has been taken
    setFlagActionTaken(true);
    
    // Set the current team with flag to trigger reformulation mode
    setCurrentTeamWithFlag(message.teamId);
    
    // Add message ID to the set of messages with reformulation requested
    if (message.id) {
      setReformulationRequestedMessages(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.add(String(message.id));
        return newSet;
      });
    }
    
    // Save the reformulation request to the server
    try {
      // Use authenticated api instance instead of plain fetch
      const response = await api.post(`/debates/${currentDebate.id}/reformulation-request`, {
        teamId: message.teamId
      });
      
      if (response.status === 200) {
        // Show success toast to the requesting team
        toast.success(t('reformulationRequestSentText'));
        logger.info(`DebateRoomPage: Reformulation request saved to server for team ${message.teamId}`);
      } else {
        toast.error(t('failedSendReformulationText'));
        logger.error('DebateRoomPage: Failed to save reformulation request to server');
        // Reset flag action taken state if there was an error
        setFlagActionTaken(false);
      }
    } catch (error) {
      logger.error('DebateRoomPage: Error saving reformulation request:', error);
      toast.error(t('failedSendReformulationText'));
      // Reset flag action taken state if there was an error
      setFlagActionTaken(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t stable
  }, [currentDebate, setCurrentTeamWithFlag]);

  // Handler for team to voluntarily choose to reformulate their flagged message
  const handleSelfReformulation = useCallback(async (message: Message) => {
    if (!currentDebate || !message || !message.teamId || !message._id) {
      logger.error('DebateRoomPage: Invalid message for self-reformulation', message);
      return;
    }
    
    logger.info('DebateRoomPage: Starting self-reformulation process for team:', message.teamId);
    
    // Set the reformulation requested state FIRST to prevent UI flicker
    setReformulationRequested(true);
    
    // Set the current team with flag to trigger reformulation mode for self
    setCurrentTeamWithFlag(message.teamId);
    
    // Mark this reformulation as self-initiated locally
    setSelfInitiatedReformulationTeamId(message.teamId);
    
    // Add message ID to the set of messages with reformulation requested
    if (message.id) {
      setReformulationRequestedMessages(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.add(String(message.id));
        return newSet;
      });
    }
    
    // Save the self-reformulation request to the server
    try {
      logger.info('DebateRoomPage: Sending self-reformulation request to server');
      const response = await api.post(`/debates/${currentDebate.id}/reformulation-request`, {
        teamId: message.teamId,
        selfInitiated: true
      });
      
      if (response.status === 200) {
        // Show success toast to the initiating team
        toast.success(t('youChosenReformulateToastText'));
        logger.info(`DebateRoomPage: Self-reformulation initiated for team ${message.teamId}`);
      } else {
        logger.error('DebateRoomPage: Failed to save self-reformulation request to server');
        toast.error(t('failedInitiateReformulationText'));
        
        // Reset states on failure
        setReformulationRequested(false);
        setCurrentTeamWithFlag(null);
        setSelfInitiatedReformulationTeamId(null);
      }
    } catch (error) {
      logger.error('DebateRoomPage: Error in self-reformulation request:', error);
      toast.error(t('failedInitiateReformulationText'));
      
      // Reset states on error
      setReformulationRequested(false);
      setCurrentTeamWithFlag(null);
      setSelfInitiatedReformulationTeamId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t stable
  }, [currentDebate]);

  // Handle closing the flag dialog properly
  const handleCloseFlagDialog = useCallback(() => {
    logger.info('DebateRoomPage: Closing flag dialog PERMANENTLY');
    
    // Set flag to prevent reopening
    setFlagDialogOpen(false);
    setFlagActionTaken(true);
    
    // Add the current message to processed IDs
    if (currentDebate?.messages?.length && selectedFlag) {
      const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
      setProcessedMessageIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.add(String(lastMessage.id));
        return newSet;
      });
    }
    
    // Clear selected flag
    setSelectedFlag(null);
    
    // Clear message input to encourage reformulation
    if (selectedFlag && currentTeamWithFlag === selectedTeam) {
      // setMessageInput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t stable
  }, [currentDebate?.messages, selectedFlag, currentTeamWithFlag, selectedTeam]);

  // Handle lifting a flag
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLiftFlag = useCallback(async () => {
    if (!selectedFlag) {
      logger.info('DebateRoomPage: No flag selected');
      return;
    }
    
    if (!id) {
      logger.info('DebateRoomPage: No debate ID');
      return;
    }
    
    // Get flag ID and fall back to using _id if id is not available
    const flagId = selectedFlag.id || selectedFlag._id;
    if (!flagId) {
      logger.info('DebateRoomPage: Flag has no ID');
      return;
    }

    if (!selectedFlag.messageId) {
      logger.info('DebateRoomPage: Flag has no messageId');
      toast.error(t('errorLiftingFlagMissingIdText'));
      return;
    }
    
    logger.info('DebateRoomPage: Lifting flag - details:', {
      debateId: id,
      messageId: selectedFlag.messageId,
      flagId: flagId,
      fullFlag: selectedFlag
    });
    
    try {
      // Close the dialog first for better UX
      handleCloseFlagDialog();
      
      // Then lift the flag
      await liftFlag(id, selectedFlag.messageId, flagId);
      
      // Show success message
      toast.success(t('flagLiftedText'));
    } catch (error) {
      logger.error('DebateRoomPage: Error lifting flag:', error);
      toast.error(t('errorLiftingFlagText'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t, setCurrentTeamWithFlag stable
  }, [selectedFlag, id, liftFlag, handleCloseFlagDialog]);

  // Add useEffect to check if debate is completed and scroll to summary
  useEffect(() => {
    if (currentDebate?.status === 'completed') {
      logger.info('DebateRoomPage: Detected completed debate, scrolling to summary');
      // Scroll to top of page to show the summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Only show the dialog if specifically requested
      if (!showDebateOverDialog && false) { // Disabled automatic dialog opening
        setShowDebateOverDialog(true);
      }
    }
  }, [currentDebate, showDebateOverDialog]);
  
  // Add useEffect to detect if we're viewing a completed debate
  useEffect(() => {
    // If the debate is loaded and it's completed, scroll to the summary
    if (currentDebate && currentDebate.status === 'completed' && !loadedDebateRef.current) {
      logger.info('DebateRoomPage: Loaded a completed debate, scrolling to summary');
      // Scroll to the top of the page to show the summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Only show the dialog if explicitly requested
      // setShowDebateOverDialog(true); // Disabled automatic dialog opening
      
      // Mark that we've handled this debate load
      loadedDebateRef.current = true;
    }
    
    // Reset the flag when the debate ID changes
    return () => {
      loadedDebateRef.current = false;
    };
  }, [currentDebate]);
  
  // Helper function to truncate text
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Handler for clicking on a flag indicator
  const handleFlagClick = useCallback((message: Message) => {
    const msgId = message.id || (message as any)._id;
    if (activeFlagMessage === msgId) {
      setActiveFlagMessage(null);
    } else {
      if (msgId) setActiveFlagMessage(msgId);
      // If dialog not already open, open it
      if (message.flags && message.flags.length > 0) {
        // Find first unlifted flag
        const unliftedFlag = message.flags.find(flag => !flag.lifted);
        if (unliftedFlag) {
          setSelectedFlag(unliftedFlag);
          setFlagDialogOpen(true);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t stable
  }, [activeFlagMessage]);
  
  // Memoize the renderMessage function to prevent unnecessary recalculations
  const renderMessage = useCallback((message: Message) => {
    // No debug logs for every message render - this is very noisy
    
    // Ensure message.flags exists
    if (!message.flags) {
      message.flags = [];
    }
    
    // Get count of unlifted flags
    const unliftedFlagsCount = message.flags.filter(flag => !flag.lifted).length;

    // Persist across refresh: local Set is emptied on reload, but server stores the decision.
    // Live teammate sync: bottom banner already uses currentTeamWithFlag — buttons must too
    // (renderMessage previously omitted it from deps, so Y kept stale buttons).
    const lastMsg = currentDebate?.messages?.[currentDebate.messages.length - 1];
    const isLatestMessage =
      !!lastMsg &&
      String(message.id || (message as any)._id) === String(lastMsg.id || (lastMsg as any)._id);
    const flagsMarkReformulationRequested = message.flags.some(
      (f: any) => !f.lifted && f.reformulationRequested === true
    );
    const serverMarksReformulationRequested =
      isLatestMessage &&
      !!currentDebate?.reformulationRequest?.teamId &&
      String(currentDebate.reformulationRequest.teamId) === String(message.teamId) &&
      !(currentDebate.reformulationRequest as any)?.selfInitiated;
    const liveMarksReformulationRequested =
      isLatestMessage &&
      !!currentTeamWithFlag &&
      String(currentTeamWithFlag) === String(message.teamId) &&
      String(message.teamId) !== String(selectedTeam) &&
      String(selfInitiatedReformulationTeamId || '') !== String(message.teamId);
    const reformulationAlreadyRequested =
      reformulationRequestedMessages.has(String(message.id)) ||
      reformulationRequestedMessages.has(String((message as any)._id)) ||
      flagsMarkReformulationRequested ||
      serverMarksReformulationRequested ||
      liveMarksReformulationRequested;
    
    // Only log if there are flags and we're in development
    if (unliftedFlagsCount > 0 && process.env.NODE_ENV === 'development') {
      debugLog(`Message has ${unliftedFlagsCount} unlifted flags`);
    }
    
    // Determine if the message is from the current user's team
    const isFromCurrentTeam = message.teamId === selectedTeam;
    
    // Get team name for display
    const teamName = getTeamName(message.teamId, currentDebate);
    
    // Get sender name - might be a user or a team name
    let senderName = message.senderId ? getUserName(message.senderId, currentDebate, user) : teamName;
    
    // If we have usernames and a sender ID, use the actual username
    if (message.senderId && usernames[message.senderId]) {
      senderName = usernames[message.senderId];
    }
    
    // Style based on team, flags, and processing state
    let messageStyle: any = {
      my: 1,
      p: 2,
      borderRadius: 2,
      maxWidth: '80%',
      position: 'relative',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
    };
    
    // Message is being processed by AI - show processing state
    if (message.isProcessing) {
      messageStyle = {
        ...messageStyle,
        ml: isFromCurrentTeam ? 'auto' : null,
        mr: isFromCurrentTeam ? null : 'auto',
        bgcolor: isFromCurrentTeam ? '#e8eaf6' : '#f3e5f5', // Light indigo/purple background
        borderLeft: '4px dashed #5c6bc0', // Dashed border for processing
        opacity: 0.8, // Slightly faded
      };
    } else if (isFromCurrentTeam) {
      messageStyle = {
        ...messageStyle,
        ml: 'auto',
        bgcolor: unliftedFlagsCount > 0 ? '#f3e5f5' : '#e3f2fd',
        borderLeft: unliftedFlagsCount > 0 ? '4px solid #9c27b0' : 'none'
      };
    } else {
      messageStyle = {
        ...messageStyle,
        mr: 'auto',
        bgcolor: unliftedFlagsCount > 0 ? '#f3e5f5' : '#f5f5f5',
        borderLeft: unliftedFlagsCount > 0 ? '4px solid #9c27b0' : 'none'
      };
    }
    
    return (
      <Box
        key={`message-box-${message.id || (message as any)._id}`}
        id={`message-${message.id || (message as any)._id}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%'
        }}
      >
        {/* Team name */}
        <Typography
          variant="caption"
          sx={{
            ml: isFromCurrentTeam ? 'auto' : 2,
            mr: isFromCurrentTeam ? 2 : 'auto',
            fontWeight: 'bold',
            color: isFromCurrentTeam ? 'primary.main' : 'secondary.main'
          }}
        >
          {teamName}
          </Typography>
          
        {/* Message content */}
        <Paper sx={messageStyle}>
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
            dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content, { unverifiedLinkNote: t('unverifiedLinkNote') }) }}
          />
            {message.isProcessing && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={16} thickness={5} />
                <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic' }}>
                  {t('analyzingText')}
                </Typography>
              </Box>
            )}
          
          {/* Flag indicator */}
          {/* Red circle indicator removed per user request */}
          
          {/* Flag details (when clicked) */}
          {unliftedFlagsCount > 0 && !message.isProcessing && (
            <Box sx={{ mt: 2 }}>
              {message.flags.filter(f => !f.lifted).map((flag, index) => (
            <Alert 
                  key={flag._id || flag.id || `${message.id}-${flag.category}-${index}`}
                  severity={flag.category === 'shitmove' ? "error" : "warning"}
              sx={{ 
                    mb: 1,
                mr: isFromCurrentTeam ? 'auto' : null,
                ml: isFromCurrentTeam ? null : 'auto',
                maxWidth: '80%',
                fontSize: '0.875rem'
              }}
            >
                <Typography variant="body2" component="div">
                    <strong>{flag.category === 'shitmove' ? t('jestersGauntletDetectedText') : t('reformulationNeededText')}</strong>
                    {' '}
                    {flag.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Issue'}
                    <Box sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.8rem' }}>
                      {flag.explanation || (flag as any).reason || t('thisMessageContainsText')}
                    </Box>
                    {(flag as any).recommendation && (
                      <Box sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                        <strong>{t('recommendationText')}</strong> {(flag as any).recommendation}
                      </Box>
                    )}
                </Typography>
                </Alert>
              ))}
              
              {/* Action buttons for the receiver */}
              {!isFromCurrentTeam && (
                reformulationAlreadyRequested ? (
                  // Show confirmation message for THIS specific message
                  <Box sx={{ 
                    display: 'flex', 
                    mt: 1,
                    p: 1,
                    bgcolor: '#fff3e0', // Light orange color
                    borderRadius: 1,
                    alignItems: 'center'
                  }}>
                    <InfoIcon sx={{ mr: 1, fontSize: '1rem', color: '#ed6c02' }} />
                    <Typography variant="body2">
                      {t('youRequestedReformulationText')}
                    </Typography>
                  </Box>
                ) : (
                  // Simple check: only show action buttons for most recent message with flags
                  // This ignores any other historical message
                  ((currentDebate?.messages?.length ?? 0) > 0 && 
                   message.id === currentDebate?.messages?.[currentDebate?.messages?.length - 1]?.id) ? (
                    // FIXED: Use our improved local state check instead of server data
                    // Show either self-reformulation notice or action buttons
                    (currentTeamWithFlag === message.teamId && selfInitiatedReformulationTeamId === message.teamId) ? (
                      <Box sx={{ 
                        display: 'flex', 
                        mt: 1,
                        p: 1,
                        bgcolor: '#e3f2fd', // Light blue color for self-initiated
                        borderRadius: 1,
                        alignItems: 'center'
                      }}>
                        <InfoIcon sx={{ mr: 1, fontSize: '1rem', color: '#2196f3' }} />
                        <Typography variant="body2">
                          {t('otherTeamChosenReformulateText')}
                        </Typography>
                      </Box>
                    ) : (
                      // Standard action buttons for the most recent message
                      (currentDebate?.cubeChallenge?.isCubeChallenge ? (
                        // For CUBE: hide reformulation controls and show helper note
                        <Box sx={{ 
                          display: 'flex', 
                          mt: 1,
                          p: 1,
                          bgcolor: '#fff3e0',
                          borderRadius: 1,
                          alignItems: 'center'
                        }}>
                          <InfoIcon sx={{ mr: 1, fontSize: '1rem', color: '#ed6c02' }} />
                          <Typography variant="body2">
                            {t('aiWillRespondAutomaticallyText')}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            justifyContent: isFromCurrentTeam ? 'flex-end' : 'flex-start',
                            mt: 1 
                          }}>
                            <Button 
                              size="small" 
                              variant="contained"
                              color="success"
                              onClick={() => handleLiftAllFlags(message)}
                            >
                              {t('liftAllFlagsText')}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              onClick={() => handleRequestReformulation(message)}
                            >
                              {t('requestReformulationText')}
                            </Button>
                          </Box>
                          {/* Note handled below globally per message */}
                        </>
                      ))
                    )
                  ) : null
                )
              )}
            </Box>
          )}
          
          {/* Collapsed note for lifted flags under the message (single authoritative block) */}
          {(() => {
            const key = String(message.id || (message as any)._id);
            const flagsArr = (message.flags || []) as any[];
            const hasFlags = flagsArr.length > 0;
            const allFlagsLifted = hasFlags && flagsArr.every(f => !!f.lifted);
            const localNote = liftedNotes ? liftedNotes[key] : undefined;
            if (!allFlagsLifted && !localNote) return null;

            const mostRecent = [...flagsArr].filter(f => f.lifted).sort((a: any, b: any) => {
              const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
              const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
              return tb - ta;
            })[0];
            const liftedById = (localNote?.by && typeof localNote.by === 'string' && localNote.by.match(/^[0-9a-fA-F]{24}$/))
              ? localNote.by
              : (mostRecent && (mostRecent.liftedBy || mostRecent.lifted_by));
            // Prefer cached username map (covers teammates who aren't team.userId); getUserName alone falls back to truncated IDs
            const byName = localNote?.by && !localNote.by.match?.(/^[0-9a-fA-F]{24}$/)
              ? localNote.by
              : (liftedById
                  ? (usernames[String(liftedById)] || getUserName(String(liftedById), currentDebate, user) || String(liftedById))
                  : t('otherTeamRequestedReformulateText'));

            const expanded = !!(localNote?.expanded);
            const flagsForList = localNote?.flags && localNote.flags.length > 0 ? localNote.flags : flagsArr;

            const handleToggle = () => {
              // Update state immediately and force a micro-task render to avoid races with background refetch
              setLiftedNotes(prev => ({
                ...prev,
                [key]: {
                  by: byName,
                  at: localNote?.at || new Date().toISOString(),
                  flags: flagsForList,
                  expanded: !expanded
                }
              }));
              if (typeof window !== 'undefined' && typeof (window as any).requestAnimationFrame === 'function') {
                (window as any).requestAnimationFrame(() => {
                  // no-op, ensures React flushes update before any async refresh
                });
              }
            };

            return (
              <Box sx={{ mt: 1 }}>
                <Alert severity="success" onClick={handleToggle} sx={{ cursor: 'pointer', bgcolor: '#e8f5e9' }}>
                  <Typography variant="body2">
                    {t('allFlagsLiftedByUserText', { username: byName })}
                  </Typography>
                </Alert>
                {expanded && (
                  <Box sx={{ mt: 1, pl: 2 }}>
                    {flagsForList.map((f: any, idx: number) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                        - {(f.type || f.category || 'flag').toString().replace(/_/g, ' ')}{f.explanation ? `: ${f.explanation}` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* Sender information and timestamp combined */}
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mt: 1, 
              textAlign: isFromCurrentTeam ? 'right' : 'left',
              color: 'text.secondary'
            }}
          >
            {t('byText')} {senderName}, {new Date(message.timestamp || Date.now()).toLocaleTimeString()}
          </Typography>
        </Paper>
      </Box>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTurn in closure
  }, [selectedTeam, currentDebate, user, handleFlagClick, handleLiftAllFlags, handleRequestReformulation, handleSelfReformulation, activeFlagMessage, reformulationRequestedMessages, usernames, liftedNotes, t, currentTeamWithFlag, selfInitiatedReformulationTeamId]);

  // Get next team to speak - important for turn management
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getNextTeamToSpeak = useCallback(() => {
    // Make sure debate and teams exist
    if (!currentDebate) return null;
    if (!currentDebate.teams || !Array.isArray(currentDebate.teams) || currentDebate.teams.length === 0) {
      return null;
    }
    
    // If there are no messages, the first team should speak
    if (!currentDebate.messages || !Array.isArray(currentDebate.messages) || currentDebate.messages.length === 0) {
      return currentDebate.teams[0].id;
    }
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    const teams = currentDebate.teams;
    
    // Check if the last message has any unlifted flags (shitmoves)
    const hasUnliftedFlags = lastMessage.flags?.some(flag => !flag.lifted) || false;
    
    if (hasUnliftedFlags) {
      // When a message has unlifted flags, the team that made the shitmove must reformulate
      // Return that team's ID as the one that should speak next
      return lastMessage.teamId;
    }
    
    // Check if this is a reformulated message (no unlifted flags)
    const isReformulatedMessage = lastMessage.isReformulation;
    
    // Normal case: Find the index of the team that just spoke
    const lastTeamIndex = teams.findIndex(t => t.id === lastMessage.teamId);
    
    // If team not found, default to first team
    if (lastTeamIndex === -1) {
      return teams[0].id;
    }
    
    // If this is a reformulated message, the other team should speak next
    if (isReformulatedMessage) {
      return teams[(lastTeamIndex + 1) % teams.length].id;
    }
    
    // For regular messages, continue with normal turn order
    return teams[(lastTeamIndex + 1) % teams.length].id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTeamWithFlag, selfInitiatedReformulationTeamId in logic
  }, [currentDebate, currentTeamWithFlag, selfInitiatedReformulationTeamId]);

  // Check if it's the current user's team's turn
  const isCurrentTeamTurn = useCallback(() => {
    debugLog('Checking if it is current team turn', {
      selectedTeam,
      currentDebate: currentDebate?.id,
      messageCount: currentDebate?.messages?.length,
      currentTeamWithFlag,
      selfInitiatedReformulationTeamId,
      lastMessageIsReformulation: currentDebate?.messages?.length ? 
        currentDebate.messages[currentDebate.messages.length - 1].isReformulation : false
    });

    // Early return if no debate or selected team
    if (!currentDebate || !selectedTeam) {
      debugLog('No debate or selected team');
        return false;
      }

    // If there are no messages yet, only the first team can start
    if (!currentDebate.messages || currentDebate.messages.length === 0) {
      const isFirstTeam = String(selectedTeam) === String(currentDebate.teams[0]?.id);
      debugLog(`No messages yet. ${isFirstTeam ? 'Your team can start' : 'Waiting for first team'}`);
      return isFirstTeam;
    }

    // Get the last message and team info
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    const hasUnliftedFlags = lastMessage.flags?.some(flag => !flag.lifted) || false;
    const isMessageOwner = String(lastMessage.teamId) === String(selectedTeam);
    
    // CRITICAL: Check if the last message is still being processed by AI
    // If it is, no one should be able to respond yet
    if (lastMessage.isProcessing) {
      debugLog('Last message is still being processed by AI - no one can respond yet', {
        messageId: lastMessage.id || lastMessage._id,
        isProcessing: lastMessage.isProcessing,
        content: lastMessage.content?.substring(0, 30)
      });
      return false;
    }
    
    // Log the processing state for debugging
    debugLog('Processing state check:', {
      messageId: lastMessage.id || lastMessage._id,
      isProcessing: lastMessage.isProcessing,
      content: lastMessage.content?.substring(0, 30)
    });
    
    // CRITICAL: Log the message's flags for debugging - keep this one but use our function
    if (hasUnliftedFlags) {
      debugLog('Last message has unlifted flags:', {
        messageId: lastMessage.id,
        isReformulation: lastMessage.isReformulation,
        messageOwner: lastMessage.teamId,
        currentTeam: selectedTeam,
        isOwner: isMessageOwner,
        currentTeamWithFlag,
        flags: lastMessage.flags
      });
    }

    // SIMPLIFIED LOGIC: Remove the complex special case handling for reformulated messages
    // When a reformulated message gets flagged again, treat it exactly like any other flagged message
    
    // Case 1: Message has unlifted flags - either needs reformulation or flag handling
    if (hasUnliftedFlags) {
      // 1a: Reformulation has been explicitly requested (either by opponent or self-initiated)
      if (currentTeamWithFlag && currentTeamWithFlag === lastMessage.teamId) {
        // Original team's turn to reformulate
        const shouldReformulate = isMessageOwner;
    
        // Check for both reformulationRequested state and selfInitiatedReformulationTeamId
        // Either should enable the team to reformulate their message
        if (String(currentTeamWithFlag) === String(selectedTeam) && 
            (reformulationRequested || selfInitiatedReformulationTeamId === selectedTeam)) {
          debugLog('Self-reformulation active - giving turn to reformulate');
    return true;
    }
    
        debugLog(`Reformulation requested. ${shouldReformulate ? 'Your turn to reformulate' : 'Waiting for other team to reformulate'}`);
        return shouldReformulate;
      }
      
      // 1b: If the message with flags is mine and no reformulation has been requested, I should NOT get a turn
      if (isMessageOwner) {
        debugLog('My message has flags, waiting for opponent to handle them');
      return false;
    }
    
      // 1c: Flags present but no reformulation requested - opposing team must handle flags
      const shouldHandleFlags = !isMessageOwner;
      debugLog(`Unlifted flags present. ${shouldHandleFlags ? 'Your turn to handle flags' : 'Waiting for other team to handle flags'}`);
      return shouldHandleFlags;
    }
    
    // Case 2: No unlifted flags - decide whose turn it is
    
    // The key issue: If this was a reformulation, turn should go to the OTHER team
    // Regardless of whether this was the first or second reformulation
    if (lastMessage.isReformulation) {
      // After a reformulation, the OTHER team gets to respond
      const otherTeamsTurn = !isMessageOwner;
      debugLog(`After reformulation. ${otherTeamsTurn ? 'Your team\'s turn to respond' : 'Other team\'s turn to respond'}`);
      return otherTeamsTurn;
    }
    
    // Regular case - alternate turns
    const isRegularTurn = !isMessageOwner;
    debugLog(`Regular turn. ${isRegularTurn ? 'Your turn' : 'Not your turn'}`);
    return isRegularTurn;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selfInitiatedReformulationTeamId in logic
  }, [currentDebate, selectedTeam, currentTeamWithFlag, reformulationRequested]);

  // Helper function to determine if the winning team exists
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getWinningTeam = useCallback((): Team | null => {
    if (!currentDebate || !currentDebate.teams) return null;
    if (currentDebate.status !== 'completed') return null;
    
    // If there's a winner explicitly set in the debate
    if (currentDebate.winner) {
      const winningTeam = currentDebate.teams.find(team => team.id === currentDebate.winner);
      return winningTeam || null;
    }
    
    return null;
  }, [currentDebate]);

  // Function to check if debate is over
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isDebateOver = useCallback((): boolean => {
    if (!currentDebate) return false;
    return currentDebate.status === 'completed';
  }, [currentDebate]);

  // Function to check if we need to lift flags
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const needsToLiftFlags = useCallback(() => {
    if (!currentDebate || !currentDebate.messages || !selectedTeam) return false;
    
    const messagesWithUnliftedFlags = currentDebate.messages.filter(message => {
      const hasUnliftedFlags = message.flags?.some(flag => !flag.lifted) || false;
      return hasUnliftedFlags && message.teamId === selectedTeam;
    });
    
    return messagesWithUnliftedFlags.length > 0;
  }, [currentDebate, selectedTeam]);

  // Function to get messages with unlifted flags
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getMessagesWithUnliftedFlags = useCallback(() => {
    if (!currentDebate || !currentDebate.messages) return [];
    
    return currentDebate.messages.filter(message => {
      const hasUnliftedFlags = message.flags?.some(flag => !flag.lifted) || false;
      return hasUnliftedFlags;
    });
  }, [currentDebate]);

  // Function to scroll to a specific message
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const scrollToMessage = useCallback((messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTurn not needed for scroll
  }, []);


  // Function to check if a team has lost due to excessive penalties
  const hasTeamLost = useCallback((teamId: string): boolean => {
    if (!currentDebate || !currentDebate.teams) return false;
    
    // Check if the team has been explicitly marked as the loser
    if (currentDebate.status === 'completed' && currentDebate.winner) {
      return teamId !== currentDebate.winner;
    }
    
    // Check if the team has accumulated too many penalties (5 or more)
    const teamPenalties = shitmoveCounts[teamId]?.total || 0;
    const penaltyLimit = 5; // Maximum allowed penalties
    
    if (teamPenalties >= penaltyLimit) {
    return true;
    }
    
    // Check if the team has 3 or more shitmoves of the same kind
    const byTypeCount = shitmoveCounts[teamId]?.byType || {};
    const sameTypePenaltyLimit = 3; // Maximum allowed penalties of the same type
    
    // Check each type to see if any exceeds the limit
    const hasTooManySameTypePenalties = Object.values(byTypeCount).some(count => 
      count >= sameTypePenaltyLimit
    );
    
    if (hasTooManySameTypePenalties) {
      return true;
    }
    
    return false;
  }, [currentDebate, shitmoveCounts]);

  // Helper function to scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Ensure we align the end of the scroll target to the bottom of the
      // message pane (important on mobile with nested scroll containers).
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  // Display teams with clear markers showing which one is your team
  const renderTeamsBanner = useCallback(() => {
    if (!currentDebate || !currentDebate.teams) return null;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2, mb: 2 }}>
        {currentDebate.teams.map((team) => {
          // Convert IDs to strings for safer comparison
          const isUserTeam = selectedTeam ? String(team.id) === String(selectedTeam) : false;
          const isFirstTeam = currentDebate.teams[0] ? String(team.id) === String(currentDebate.teams[0].id) : false;
          const isTeamTurn = isCurrentTeamTurn() && isUserTeam;
          
          // Display debugging info in dev mode - use debugLog to reduce noise
          if (isUserTeam) { // Only log for user's team to reduce spam
            debugLog(`Team: ${team.name}`, {
              teamId: String(team.id),
              isUserTeam,
              isFirstTeam,
              isTeamTurn,
              userId: team.userId ? String(team.userId) : null,
              selectedTeam: selectedTeam ? String(selectedTeam) : null,
              userMatches: team.userId && user?.id ? String(team.userId) === String(user.id) : false,
              members: team.members || []
            });
          }
          
          // Check if user is the creator of the debate
          const isCreator = !!(currentDebate.createdBy && user?.id && 
            String(currentDebate.createdBy) === String(user.id));
          
          // Find the opponent team (the other team)
          const opponentTeam = currentDebate.teams.find(t => String(t.id) !== String(team.id));
          
          return (
            <Box key={team.id} sx={{ width: { xs: '100%', md: '48%' }, minWidth: 0 }}>
              <TeamInfo 
                team={team} 
                isUserTeam={isUserTeam} 
                isCurrentTurn={isCurrentTeamTurn() && String(currentTurn) === String(team.id)}
                usernames={usernames}
                        yourTeamText={t('yourTeamHeader')}
        teamMembersText={t('teamMembersText')}
        currentTurnText={t('currentTurnText')}
        waitingText={t('waitingText')}
                isCreator={isCreator}
                debateId={currentDebate.id}
                opponentTeam={opponentTeam}
                opponentInviteLink={currentDebate.inviteLink}
              />
            </Box>
          );
        })}
      </Box>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTurn in closure
  }, [currentDebate, selectedTeam, user, isCurrentTeamTurn, usernames, t]);
  
  // Fix auto-scrolling on page load
  // Only scroll on new messages, not on initial load
  const lastMessageContentLength = currentDebate?.messages?.length
    ? ((currentDebate.messages[currentDebate.messages.length - 1] as any)?.content?.length ?? 0)
    : 0;

  useEffect(() => {
    // Only run if we have messages and it's not the initial load
    if (currentDebate?.messages && prevMessageCountRef.current > 0) {
      const prevContentLength = prevLastMessageContentLengthRef.current;
      const didNewMessage = prevMessageCountRef.current < currentDebate.messages.length;
      const didLatestContentGrow = lastMessageContentLength > prevContentLength;

      // Only scroll if message count has increased (new message added)
      if (didNewMessage || didLatestContentGrow) {
        logger.info('DebateRoomPage: New message detected, scrolling to bottom');
        scrollToBottom();
      }
      // Update the previous count
      prevMessageCountRef.current = currentDebate.messages.length;
      prevLastMessageContentLengthRef.current = lastMessageContentLength;
    } else if (currentDebate?.messages) {
      // Just set the initial count without scrolling on first load
      logger.info('DebateRoomPage: Initial messages loaded, setting count without scrolling');
      prevMessageCountRef.current = currentDebate.messages.length;
      prevLastMessageContentLengthRef.current = lastMessageContentLength;
      // Mobile users often start at the top and never reach the bottom; ensure
      // the latest response is visible on initial load.
      setTimeout(() => scrollToBottom(), 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentDebate.messages in closure
  }, [currentDebate?.messages?.length, lastMessageContentLength]);
  
  // Fetch debate data on mount - only needs to run once
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | undefined;
    let attemptCount = 0;
    const maxAttempts = 3;
    
    logger.info('DebateRoomPage: Setting up fetch effect');
    
    // Only load debate if we haven't already loaded it successfully
    if (loadedDebateRef.current) {
      logger.info('DebateRoomPage: Debate already loaded, skipping fetch');
      return;
    }
    
    const loadDebate = async () => {
      if (!id) {
        logger.error('DebateRoomPage: No debate ID provided');
        setError('No debate ID provided');
        return;
      }
      
      if (!isMounted) return;
      
      if (attemptCount >= maxAttempts) {
        logger.error(`DebateRoomPage: Max attempts (${maxAttempts}) reached trying to fetch debate`);
        setError(`Failed to fetch debate after ${maxAttempts} attempts`);
        return;
      }
      
      attemptCount++;
      logger.info(`DebateRoomPage: Attempt ${attemptCount}/${maxAttempts} to fetch debate with ID:`, id);
      
      try {
        setFetchError(false); // Reset fetch error
        await fetchDebateById(id);
        logger.info('DebateRoomPage: Successfully fetched debate');
        
        // Mark as loaded so we don't fetch again
        loadedDebateRef.current = true;
        
        // Check if socket is connected
        if (socket) {
          logger.info('DebateRoomPage: Socket status after fetch:', socket.connected ? 'connected' : 'disconnected');
        }
      } catch (err) {
        logger.error('DebateRoomPage: Failed to fetch debate:', err);
        if (isMounted) {
          setFetchError(true);
          setError('Failed to fetch debate. Please try again later.');
          
          // Try again after a delay with exponential backoff
          if (attemptCount < maxAttempts) {
            const delay = Math.pow(2, attemptCount) * 1000; // 2s, 4s, 8s
            logger.info(`DebateRoomPage: Retrying in ${delay}ms`);
            timeoutId = setTimeout(loadDebate, delay);
          }
        }
      }
    };

    // Start the initial fetch
    timeoutId = setTimeout(loadDebate, 300);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      logger.info('DebateRoomPage: Cleanup effect - unmounting');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentDebate/socket?.connected would cause re-fetch loops
  }, [id, fetchDebateById, socket]);
  
  // Add WebSocket reconnection useEffect after the fetchDebateById useEffect
  useEffect(() => {
    if (!currentDebate || !id || !user?.id) return;
    
    // Check if socket is connected or not
    const isSocketConnected = socket?.connected === true;
    
    // If socket not connected, this is a good time to trigger reconnection
    if (!isSocketConnected) {
      logger.info('DebateRoomPage: Socket not connected when entering debate room, forcing reconnection...');
      
      // DEBOUNCE: Use a simple timestamp to prevent excessive fetching
      const now = Date.now();
      const lastReconnectFetch = (window as any).lastDebateReconnectFetch || 0;
      
      if (now - lastReconnectFetch > 10000) { // Only fetch if last fetch was more than 10 seconds ago
        (window as any).lastDebateReconnectFetch = now;
        fetchDebateById(id, true).catch(err => {
          logger.error('DebateRoomPage: Error refreshing debate during socket reconnection:', err);
        });
      } else {
        logger.info('DebateRoomPage: Skipping debate fetch - too soon since last reconnect fetch');
      }
    } else {
      logger.info('DebateRoomPage: Socket already connected in debate room');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentDebate/selectedTeam/user in closure
  }, [currentDebate?.id, id, user?.id, fetchDebateById]);
  
  // Set user's team based on their ID
  useEffect(() => {
    if (!currentDebate) {
      return; // Don't proceed without debate data
    }

    // CRITICAL: Debug user data to see if it's fully loaded
    logger.info('DebateRoomPage: User information for team assignment:', {
      id: user?.id, 
      userId: user?.id,
      username: user?.username,
      userObj: user,
      userIdType: user?.id ? typeof user.id : 'undefined',
      actualIdValue: user?.id,
      isUserLoaded: !!user?.id,
      currentSelectedTeam: selectedTeam
    });

    // Ensure we have valid teams
    if (!currentDebate.teams || !Array.isArray(currentDebate.teams) || currentDebate.teams.length === 0) {
      logger.error('DebateRoomPage: No teams available in debate');
      return;
    }
    
    // If we don't have user data yet, we can't make a proper assignment
    if (!user || !user.id) {
      // If we haven't made any selection yet, use the first team temporarily
      if (!selectedTeam) {
        logger.info('DebateRoomPage: User data not available, selecting first team temporarily');
        setSelectedTeam(currentDebate.teams[0].id);
      }
      return;
    }
    
    // Now that we have user data, we can make a proper team assignment
    // Get current user ID as string for exact comparison
    const currentUserId = String(user.id);
    
    // Get detailed debugging information about teams
    logger.info('DebateRoomPage: Teams with user data:', currentDebate.teams.map(team => ({
      id: team.id,
      name: team.name,
      userId: team.userId ? String(team.userId) : null,
      members: team.members || [],
      userIdMatch: team.userId ? String(team.userId) === currentUserId : false,
      memberMatch: team.members ? team.members.some(memberId => String(memberId) === currentUserId) : false,
      comparison: team.userId ? `${String(team.userId)} === ${currentUserId}` : 'No userId set'
    })));
    
    // First, try to find user's team by checking members array (for multiple users support)
    let userTeam = currentDebate.teams.find(team => 
      team.members && Array.isArray(team.members) && 
      team.members.some(memberId => String(memberId) === currentUserId)
    );
    
    // If not found in members, fallback to checking userId field for backward compatibility
    if (!userTeam) {
      userTeam = currentDebate.teams.find(team => {
        // Convert team userId to string for safe comparison
        const teamUserId = team.userId ? String(team.userId) : null;
        return teamUserId && teamUserId === currentUserId;
      });
    }
      
      if (userTeam) {
      logger.info('DebateRoomPage: Found user team:', userTeam.name);
        setSelectedTeam(userTeam.id);
    } else {
      logger.info('DebateRoomPage: User team not found, assigning based on creator status');
      
      // Is user the creator?
      const creatorId = (currentDebate as any).createdBy ? String((currentDebate as any).createdBy) : null;
      const isCreator = creatorId && currentUserId === creatorId;
      
      logger.info('DebateRoomPage: Creator check:', {
        isCreator,
        createdBy: creatorId,
        userId: currentUserId,
        match: currentUserId === creatorId
      });
      
      // If user is the creator, assign to Team 1
      if (isCreator) {
        logger.info('DebateRoomPage: User is the debate creator, assigning to first team');
        setSelectedTeam(currentDebate.teams[0].id);
        
        // Also update the team's userId field in local state
        currentDebate.teams[0].userId = user.id;
        
        // Add user to members array if not already there
        if (!currentDebate.teams[0].members) {
          currentDebate.teams[0].members = [];
        }
        
        if (!currentDebate.teams[0].members.some(memberId => String(memberId) === currentUserId)) {
          currentDebate.teams[0].members.push(user.id);
        }
      } else {
        logger.info('DebateRoomPage: User is NOT debate creator, assigning to second team');
        // If user is not the creator, put them in the second team
        if (currentDebate.teams.length > 1) {
          setSelectedTeam(currentDebate.teams[1].id);
          
          // Also update the team's userId field in local state
          currentDebate.teams[1].userId = user.id;
          
          // Add user to members array if not already there
          if (!currentDebate.teams[1].members) {
            currentDebate.teams[1].members = [];
          }
          
          if (!currentDebate.teams[1].members.some(memberId => String(memberId) === currentUserId)) {
            currentDebate.teams[1].members.push(user.id);
          }
        } else {
          logger.warn('DebateRoomPage: Only one team exists, this may cause issues');
          setSelectedTeam(currentDebate.teams[0].id);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on ID changes
  }, [currentDebate?.id, user?.id]);
  
  // Log socket status whenever it changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && socket) {
      logger.info('DebateRoomPage: Socket status:', socket.connected ? 'connected' : 'disconnected');
    }
  }, [socket]);
  
  // Log when currentDebate changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.info('DebateRoomPage: currentDebate updated');
    }
  }, [currentDebate?.id]); // Only log when the debate ID changes, not the entire object
  
  // Log and check for message updates
  useEffect(() => {
    if (!currentDebate?.messages) return;
    
    logger.info(`DebateRoomPage: Messages array changed, count: ${currentDebate.messages.length}`);
    
    // If we have messages, update the turn state
    if (currentDebate.messages.length > 0) {
      // Get the latest message
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
      logger.info('DebateRoomPage: Latest message is from team:', lastMessage.teamId);
      
      // Update current turn based on the latest message
      const isMyTurn = isCurrentTeamTurn();
      logger.info(`DebateRoomPage: Based on latest message, is it my turn? ${isMyTurn}`);
      
      // Force redraw of team banners
      setCurrentTurn(isMyTurn ? selectedTeam : null);
    }
    
    // Comment out automatic scrolling
    // setTimeout(scrollToBottom, 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isCurrentTeamTurn/selectedTeam in closure
  }, [currentDebate?.messages?.length]);
  
  // Add function to update shitmove counts based on messages
  useEffect(() => {
    if (!currentDebate?.messages || !currentDebate.teams) return;
    
    // Initialize counts for each team
    const updatedCounts: {
      [teamId: string]: {
        total: number;
        byType: { [type: string]: number };
      };
    } = {};
    
    // Initialize with zero counts for all teams
    currentDebate.teams.forEach(team => {
      updatedCounts[team.id] = {
        total: 0,
        byType: {}
      };
    });
    
    // Count all unlifted flags that are categorized as "shitmove" (penalty type)
    currentDebate.messages.forEach(message => {
      if (message.flags && message.flags.length > 0) {
        const teamId = message.teamId;
        if (!teamId) return;
        
        message.flags.forEach(flag => {
          // Only count unlifted flags categorized as "shitmove" (not "reformulation")
          if (!flag.lifted && flag.category === 'shitmove') {
            // Increment total count
            updatedCounts[teamId].total = (updatedCounts[teamId].total || 0) + 1;
            
            // Increment type-specific count
            const flagType = flag.type || 'unknown';
            updatedCounts[teamId].byType[flagType] = 
              (updatedCounts[teamId].byType[flagType] || 0) + 1;
          }
        });
      }
    });
    
    setShitmoveCounts(updatedCounts);
  }, [currentDebate?.messages, currentDebate?.teams]);
  
  // Add effect to check for debate end conditions when shitmove counts change
  useEffect(() => {
    // CRITICAL FIX: Prevent multiple debate ending calls
    if (isEndingDebate) {
      logger.info('🛡️ RACE CONDITION PROTECTION: Debate ending already in progress, skipping check');
      return;
    }
    
    // Check if any team has lost due to shitmoves
    if (currentDebate?.teams && currentDebate.status === 'active') {
      for (const team of currentDebate.teams) {
        if (hasTeamLost(team.id)) {
          logger.info(`DebateRoomPage: Debate over - Team ${team.id} has exceeded shitmove limits`);
          
          // Find the winning team
          const winningTeam = currentDebate.teams.find(t => t.id !== team.id);
          if (winningTeam) {
            // CRITICAL FIX: Set flag to prevent multiple calls
            logger.info('🛡️ RACE CONDITION PROTECTION: Setting isEndingDebate flag to prevent duplicates');
            setIsEndingDebate(true);
            
            // End the debate
            endDebate(winningTeam.id, 'gauntlet_limit');
          }
          
          // Show debate over dialog
          setShowDebateOverDialog(true);
          break;
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- endDebate stable
  }, [shitmoveCounts, currentDebate?.teams, currentDebate?.status, isEndingDebate, hasTeamLost]);
  
  // Handler for opening flag dialog from a message
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onFlagClick = (message: Message) => {
    logger.info('DebateRoomPage: Flag click on message:', message);
    
    // Make sure flags exist
    if (!message.flags || !Array.isArray(message.flags) || message.flags.length === 0) {
      logger.info('DebateRoomPage: No flags found on message');
      toast('No flags found on this message');
      return;
    }
    
    const unliftedFlags = message.flags.filter(flag => !flag.lifted);
    logger.info('DebateRoomPage: Unlifted flags:', unliftedFlags);
    
    if (unliftedFlags.length > 0) {
      // We have unlifted flags, show the dialog
      setCurrentTeamWithFlag(message.teamId);
      handleOpenFlagDialog(unliftedFlags[0]);
    } else {
      logger.info('DebateRoomPage: All flags are lifted');
      toast('All flags on this message have been lifted');
    }
  };

  // Handle dismissing the flag banner
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDismissBanner = () => {
    logger.info('DebateRoomPage: Dismissing flag banner');
    setShowFlagBanner(false);
    // Keep the message in view so it can be reformulated
  };

  // Completely remove the handleOpenFlagDialog function
  const handleOpenFlagDialog = useCallback((flag: ShitmoveFlag) => {
    // Flag dialog functionality has been completely removed
    // This function is kept as a no-op to maintain React hook ordering
    logger.info('Flag dialog functionality has been removed - flags are now shown inline with messages');
    // DO NOT REMOVE THIS FUNCTION - it's needed for consistent hook ordering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional minimal deps for no-op
  }, [currentDebate?.messages, processedMessageIds]);
  
  // Handle exporting debate as JSON (reserved for future export UI)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExportJson = async () => {
    if (!id) return;
    
    setExportLoading(true);
    try {
      const blob = await exportDebateAsJson(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debate-${id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to export debate:', err);
    } finally {
      setExportLoading(false);
    }
  };
  
  // Handle concede button click
  const handleOpenConcedeDialog = () => {
    setConcedeDialogOpen(true);
  };

  // Handle concede dialog close
  const handleCloseConcedeDialog = () => {
    setConcedeDialogOpen(false);
  };

  // Add function to end the debate and update status on server
  const endDebate = async (winningTeamId: string, reason: string) => {
    if (!currentDebate) return;
    
    try {
      logger.info(`DebateRoomPage: Ending debate - Winner: ${winningTeamId}, Reason: ${reason}`);
      
      // Use updateDebateStatus to generate summary automatically and pass the reason
      const updatedDebate = await updateDebateStatus(currentDebate.id, 'completed', winningTeamId, reason);
      logger.info('DebateRoomPage: Successfully updated debate status to completed on server');
      logger.info('DebateRoomPage: Updated debate has summary:', !!updatedDebate?.summary);
      
      if (updatedDebate?.summary) {
        logger.info('DebateRoomPage: Summary preview:', updatedDebate.summary.substring(0, 100) + '...');
      } else {
        logger.warn('DebateRoomPage: No summary received in the updated debate response');
      }
      
      // Reload the debate to get the summary - force refresh to ensure we get the latest data
      try {
        logger.info('DebateRoomPage: Reloading debate to get the latest data with summary');
        await fetchDebateById(currentDebate.id, true); // Pass true to force refresh
        
        // Double-check by directly fetching
        const reloadedDebate = await getDebateById(currentDebate.id);
        logger.info('DebateRoomPage: Successfully reloaded debate');
        logger.info('DebateRoomPage: Reloaded debate has summary:', !!reloadedDebate?.summary);
        
        if (reloadedDebate?.summary) {
          logger.info('DebateRoomPage: Reloaded summary preview:', reloadedDebate.summary.substring(0, 100) + '...');
        } else {
          logger.warn('DebateRoomPage: No summary found in reloaded debate');
          
          // If we still don't have a summary, let's set a placeholder
          if (reloadedDebate && !reloadedDebate.summary) {
            reloadedDebate.summary = "This debate has been completed. A summary is not available.";
            // We don't need to save this, it's just for the UI
          }
        }
      } catch (error) {
        logger.error('DebateRoomPage: Failed to reload debate with summary:', error);
        
        // Handle case where we can't reload - use what we have
        if (currentDebate && !currentDebate.summary && updatedDebate?.summary) {
          // Copy the summary from the updatedDebate
          currentDebate.summary = updatedDebate.summary;
        }
      }
      
      // Only show the result dialog, not the debate over dialog
      // The summary will be visible on the page itself
      setDebateResultDialogOpen(true);
      
      // Scroll to the top of the page to show the summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      logger.error('DebateRoomPage: Failed to update debate status:', error);
      
      // CRITICAL FIX: Handle 409 "Conflict" as success (debate already completed)
      if ((error as any)?.response?.status === 409) {
        logger.info('🛡️ RACE CONDITION SUCCESS: Debate already completed by another request - this is actually success!');
        
        // Reload the debate to get the updated status and summary
        try {
          logger.info('DebateRoomPage: Reloading debate after 409 to get completed state');
          await fetchDebateById(currentDebate.id, true);
          
          // Show success message instead of error
          toast.success('Debate completed successfully!');
          
          // Show the result dialog
          setDebateResultDialogOpen(true);
          
          // Scroll to the top of the page to show the summary
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return; // Exit successfully
        } catch (reloadError) {
          logger.error('DebateRoomPage: Failed to reload debate after 409:', reloadError);
          // Fall through to the generic error handling below
        }
      }
      
      // Handle genuine errors (not 409 conflicts)
      logger.info('DebateRoomPage: Genuine error occurred, status:', (error as any)?.response?.status);
      
      // Try to set completed status client-side even if the server call failed
      if (currentDebate) {
        currentDebate.status = 'completed';
        
        // Set a placeholder summary 
        if (!currentDebate.summary) {
          currentDebate.summary = "This debate has been completed, but the summary could not be generated.";
        }
        
        // Set the winner
        if (currentDebate.teams) {
          const winnerTeam = currentDebate.teams.find(team => team.id === winningTeamId);
          if (winnerTeam) {
            currentDebate.winner = winningTeamId;
          }
        }
      }
      
      toast.error('There was an issue updating the debate status on the server, but the debate has been marked as completed locally.');
      
      // Show the result dialog
      setDebateResultDialogOpen(true);
      
      // Scroll to the top of the page to show the summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      // CRITICAL FIX: Reset the flag to allow future debate ending attempts if needed
      logger.info('DebateRoomPage: Resetting isEndingDebate flag');
      setIsEndingDebate(false);
    }
  };

  // Handle concede debate
  const handleConcede = async () => {
    if (!currentDebate || !selectedTeam) return;
    
    // CRITICAL FIX: Prevent multiple concession calls
    if (isEndingDebate) {
      logger.info('DebateRoomPage: Debate ending already in progress, ignoring concede');
      return;
    }
    
    try {
      setConcedeDialogOpen(false);
      logger.info('DebateRoomPage: Team has conceded:', selectedTeam);
      
      // Find the opposing team
      const opposingTeam = currentDebate.teams.find(team => team.id !== selectedTeam);
      if (!opposingTeam) {
        toast.error(t('couldNotIdentifyOpposingTeamText'));
        return;
      }
      
      try {
        // CRITICAL FIX: Set flag to prevent multiple concessions
        setIsEndingDebate(true);
        
        // End the debate with the opposing team as winner
        await endDebate(opposingTeam.id, 'user_concession');
        
        // Show success message
        toast.success(`${t('youConcededText')} ${opposingTeam.name}.`);
      } catch (endDebateError) {
        logger.error('DebateRoomPage: Failed to end debate after concession:', endDebateError);
        toast.error(t('failedEndDebateAfterConcessionText'));
        // Reset flag on error
        setIsEndingDebate(false);
      }
    } catch (error) {
      logger.error('DebateRoomPage: Failed to concede debate:', error);
      toast.error(t('failedConcedeText'));
      setConcedeDialogOpen(false);
      // Reset flag on error
      setIsEndingDebate(false);
    }
  };
  
  // Handle sending a message - now primarily handled by DebateMessageInputForm
  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || !selectedTeam || !currentDebate) return;

    try {
      logger.info('DebateRoomPage: Sending message with content:', messageText.substring(0, 50) + (messageText.length > 50 ? '...' : ''));
      
      // Make sure socket is connected to the debate room
      if (socket) {
        logger.info('DebateRoomPage: Ensuring socket connection to room:', currentDebate.id);
        socket.emit('join_debate', currentDebate.id, user?.id || 'undefined_user');
      }
      
      // Add first message check - only first team can start
      if (!currentDebate.messages || currentDebate.messages.length === 0) {
        // Convert to strings for safer comparison
        const isFirstTeam = String(selectedTeam) === String(currentDebate.teams[0]?.id || '');
        if (!isFirstTeam) {
          logger.info('DebateRoomPage: Rejecting message - only first team can start the debate', {
            selectedTeam,
            firstTeamId: currentDebate.teams[0]?.id,
            isFirstTeam
          });
          toast.error(t('onlyFirstTeamCanStartText'));
          return;
        }
        logger.info('DebateRoomPage: First team starting the debate');
      }
      
      // Check if this is a reformulation of a flagged message
      const lastMessage = currentDebate.messages?.[currentDebate.messages.length - 1];
      const lastMessageHasUnliftedFlags = 
        currentDebate.messages?.length > 0 && 
        lastMessage?.flags?.some(f => !f.lifted);
      
      // Convert to strings for safer comparison
      const isFromSameTeam = lastMessage ? String(lastMessage.teamId) === String(selectedTeam) : false;
      
      const isReformulationAttempt = lastMessageHasUnliftedFlags && isFromSameTeam;
        
      // DETAILED LOGGING: Track everything about the reformulation state
      logger.info('DebateRoomPage: DETAILED MESSAGE ANALYSIS:', {
        lastMessageTeamId: lastMessage?.teamId,
        selectedTeam,
        isFromSameTeam, 
        hasUnliftedFlags: lastMessageHasUnliftedFlags,
        isReformulationAttempt,
        currentIsReformulating: isReformulating,
        currentTeamWithFlag,
        isReformulationOfReformulation: lastMessage?.isReformulation && isReformulationAttempt,
        messageDatabase: lastMessage ? {
          id: lastMessage._id,
          content: lastMessage.content.substring(0, 30),
          isReformulation: lastMessage.isReformulation,
          flags: lastMessage.flags
        } : null
      });
      
      if (isReformulationAttempt) {
        logger.info('DebateRoomPage: Sending message as a reformulation of a flagged message');
        setIsReformulating(true);
      }
      
      // Use the sendMessage function from context
      logger.info('DebateRoomPage: Calling sendMessage with reformulation flag:', isReformulationAttempt);
      const message = await sendMessage(
        currentDebate.id, 
        messageText.trim(), 
        selectedTeam,
        isReformulationAttempt // Pass flag if this is a reformulation
      );
      logger.info('DebateRoomPage: Message sent successfully, received:', message);
      
      // Reset reformulation-related states
      setIsReformulating(false);
      setReformulationRequested(false);
      setSelfInitiatedReformulationTeamId(null);
      
      // Clear the reformulation requested state for this message to hide the buttons on Team B side
      if (isReformulationAttempt && lastMessage && (lastMessage.id || lastMessage._id)) {
        const messageId = String(lastMessage.id || lastMessage._id);
        logger.info('DebateRoomPage: Clearing reformulation requested state for message:', messageId);
        setReformulationRequestedMessages(prev => {
          // Create a new set without the completed message
          const newSet = new Set(Array.from(prev));
          newSet.delete(messageId);
          return newSet;
        });
      }
      
      // EXPANDED FIX: Always reset all reformulation state after sending any message
      // This ensures that all messages, reformulated or not, are treated exactly the same
      // and any new flags will be treated as fresh flags requiring fresh actions
      logger.info('DebateRoomPage: Resetting all reformulation state after sending message');
      setCurrentTeamWithFlag(null);
      setSelfInitiatedReformulationTeamId(null);
      setReformulationRequested(false);
      
      // Also reset reformulation state on the server
      try {
        logger.info('DebateRoomPage: Forcing server-side reset of reformulation state');
        const resetResponse = await api.post(`/debates/${currentDebate.id}/reset-reformulation`, {
          messageId: message.id || message._id,
          teamId: selectedTeam
        });
        
        if (resetResponse.status === 200) {
          logger.info('DebateRoomPage: Successfully reset reformulation state on server');
        } else {
          logger.warn('DebateRoomPage: Failed to reset reformulation state on server');
        }
      } catch (error) {
        logger.error('DebateRoomPage: Error resetting reformulation state', error);
      }
      
      // Clear input after sending - no longer needed as DebateMessageInputForm handles this
      // setMessageInput('');
      
      // Keep only the long-running analysis toast from context; no additional send toast here
      
      // Comment out automatic scrolling
      // scrollToBottom();
    } catch (error) {
      logger.error('DebateRoomPage: Error sending message:', error);
      
      // Show detailed error information
      let errorMessage = 'Error sending message. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // If it was a reformulation error, keep the reformulation state active
      if (isReformulating && String(error).includes('reformulat')) {
        logger.info('DebateRoomPage: Keeping reformulation mode active after error');
      } else {
        setIsReformulating(false);
      }
      // Rethrow so DebateMessageInputForm can preserve the input instead of clearing it
      throw error;
    }
  };

  // After fixing the main message update effect, let's add one more effect to handle flag dialogs
  // This keeps the flag dialog opening logic separate from the main message update
  useEffect(() => {
    // Only process if we have messages and flags are showing
    if (!currentDebate?.messages?.length) return;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    const unliftedFlags = lastMessage.flags?.filter(flag => !flag.lifted) || [];
    
    // Only show flag dialog for user's own team's messages
    if (unliftedFlags.length > 0 && lastMessage.teamId === selectedTeam) {
      // Set flag banner to show
      setShowFlagBanner(true);
      
      // Show dialog only if this is a new message with flags and dialog isn't already open
      if (!flagDialogOpen) {
      if (process.env.NODE_ENV === 'development') {
        logger.info('DebateRoomPage: Auto-opening flag dialog for new message with flags');
      }
      handleOpenFlagDialog(unliftedFlags[0]);
    }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-run when length/selectedTeam/dialog change
  }, [currentDebate?.messages?.length, selectedTeam, flagDialogOpen]);

  // Check authentication and handle redirect
  useEffect(() => {
    if (!isAuthenticated && id) {
      redirectAfterLogin(`/debate/${id}`);
    }
  }, [isAuthenticated, id, redirectAfterLogin]);

  // Reset reformulationRequested only when message count changes
  useEffect(() => {
    // We only want to reset these flags when new messages come in
    // NOT when currentTeamWithFlag changes during self-reformulation
    setReformulationRequested(false);
    setFlagActionTaken(false);
  }, [currentDebate?.messages?.length]);

  // Replace the callback function with a memoized value
  // Function to check if I've requested reformulation from the other team
  const hasRequestedReformulationValue = useMemo((): boolean => {
    if (!currentDebate?.messages?.length || !selectedTeam) return false;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    if (!lastMessage) return false;
    
    const hasUnliftedFlags = lastMessage.flags?.some(flag => !flag.lifted) || false;
    
    // FIXED: Check if the message is from the other team relative to the current user
    // This checks if the message is NOT from the selectedTeam (current user's team)
    const isMessageFromOtherTeam = String(lastMessage.teamId) !== String(selectedTeam);
    
    // FIXED: We should check if the flagged message is from the OTHER team and 
    // if the flag has been set on THAT team (not our team)
    // This means: we (selectedTeam) requested reformulation from the other team (lastMessage.teamId)
    const isCorrectTeamFlagged = currentTeamWithFlag === lastMessage.teamId;
    
    // CRITICAL FIX: Exclude self-initiated reformulations
    // If the reformulation was self-initiated, then WE didn't request it - the other team chose to do it
    const isSelfInitiated = selfInitiatedReformulationTeamId === lastMessage.teamId ||
                           (currentDebate?.reformulationRequest?.teamId === lastMessage.teamId && 
                            (currentDebate.reformulationRequest as any)?.selfInitiated === true);
    
    // Add debug logging to track when this function is called and why it returns what it does
    // Only log in development mode to reduce noise in production
    if (process.env.NODE_ENV === 'development') {
      logger.info('DebateRoomPage DEBUG: hasRequestedReformulation computed:', {
      hasUnliftedFlags,
      isMessageFromOtherTeam,
      teamWithFlag: currentTeamWithFlag,
      messageTeamId: lastMessage.teamId,
      isCorrectTeamFlagged,
        isSelfInitiated,
      selectedTeam,
        result: (hasUnliftedFlags && isMessageFromOtherTeam && isCorrectTeamFlagged && !isSelfInitiated)
    });
    }
    
    // FIXED LOGIC: 
    // 1. The message needs to have unlifted flags
    // 2. The message needs to be from the OTHER team (not my team)
    // 3. The flagged team must match the message team
    // 4. It must NOT be a self-initiated reformulation
    return hasUnliftedFlags && isMessageFromOtherTeam && isCorrectTeamFlagged && !isSelfInitiated;
  }, [currentDebate?.messages, selectedTeam, currentTeamWithFlag, selfInitiatedReformulationTeamId, currentDebate?.reformulationRequest]);

  // Add useEffect to handle refresh scenarios by syncing server state to local state
  useEffect(() => {
    // On page load/refresh, check if there's a reformulation request in the server data
    // and sync it to our local state to maintain UI consistency
    if (!currentDebate?.reformulationRequest) return;
    
    const serverReformRequest = currentDebate.reformulationRequest;
    const isServerSelfInitiated = (serverReformRequest as any)?.selfInitiated === true;
    
    // Only sync if we don't already have local state set
    if (isServerSelfInitiated && serverReformRequest.teamId && !selfInitiatedReformulationTeamId) {
      logger.info('DebateRoomPage: Syncing self-initiated reformulation state from server on refresh');
      setSelfInitiatedReformulationTeamId(serverReformRequest.teamId);
      
      // Also ensure currentTeamWithFlag is set
      if (!currentTeamWithFlag) {
        setCurrentTeamWithFlag(serverReformRequest.teamId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setCurrentTeamWithFlag is stable
  }, [currentDebate?.reformulationRequest, selfInitiatedReformulationTeamId, currentTeamWithFlag]);

  // Add useEffect to handle when a reformulated message gets flagged again
  useEffect(() => {
    if (!currentDebate?.messages?.length) return;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    const hasUnliftedFlags = lastMessage.flags?.some(flag => !flag.lifted) || false;
    
    // Check if this is a reformulated message that just got flagged again
    if (hasUnliftedFlags && lastMessage.isReformulation) {
      logger.info('DebateRoomPage: Detected reformulated message with new flags - resetting reformulation state');
      
      // Reset all reformulation-related state to start fresh
      // This ensures the UI shows the same "I Prefer to Reformulate" options as the first time
      setCurrentTeamWithFlag(null);
      setSelfInitiatedReformulationTeamId(null);
      setReformulationRequested(false);
    
      // Also reset the reformulation request on the server to keep state consistent
      if (currentDebate?.reformulationRequest?.teamId === lastMessage.teamId) {
        api.post(`/debates/${currentDebate.id}/reset-reformulation`, {
          messageId: lastMessage.id || lastMessage._id,
          teamId: lastMessage.teamId
        }).catch(error => {
          logger.error('DebateRoomPage: Error resetting reformulation state after re-flagging:', error);
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps to avoid loops
  }, [currentDebate?.messages?.length, currentDebate?.id]);
      
  // Add a function to check if I need to reformulate (complementary to hasRequestedReformulation)
  const needsToReformulate = useCallback((): boolean => {
    if (!currentDebate?.messages?.length || !selectedTeam) return false;
    
    // First check if the current team has been flagged to reformulate
    const result = currentTeamWithFlag === selectedTeam;
    
    // Remove excessive logging - only log in development and when result changes
    // logger.info('DebateRoomPage: needsToReformulate check:', {
    //   selectedTeam,
    //   currentTeamWithFlag,
    //   reformulationRequested,
    //   result
    // });
    
    return result;
  }, [currentDebate?.messages, selectedTeam, currentTeamWithFlag]);

  // Update the flag dialog opening effect to prevent reopening dialogs for messages we've already handled
  useEffect(() => {
    // Only process if we have messages and flags are showing
    if (!currentDebate?.messages?.length) return;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    
    // Skip if we've already processed this message or if the user has taken action
    if (processedMessageIds.has(String(lastMessage.id)) || flagActionTaken) {
      return;
    }
    
    const unliftedFlags = lastMessage.flags?.filter(flag => !flag.lifted) || [];
    
    // Only show flag dialog for user's own team's messages
    if (unliftedFlags.length > 0 && String(lastMessage.teamId) === String(selectedTeam)) {
      // Banner notification removed per user request
      
      // Mark this message as processed so we don't show the dialog again
      setProcessedMessageIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.add(String(lastMessage.id));
        return newSet;
      });
      
      // DISABLED: Never auto-open flag dialogs
      // Instead, user must manually view flags using the banner button
    }
  }, [currentDebate?.messages, selectedTeam, flagDialogOpen, flagActionTaken, processedMessageIds, reformulationRequested]);

  // COMPLETELY DISABLE the automatic flag dialog opening effect
  // Instead, just set the banner to show
  useEffect(() => {
    // Only process if we have messages and we're the team with flags
    if (!currentDebate?.messages?.length) return;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    const unliftedFlags = lastMessage.flags?.filter(flag => !flag.lifted) || [];
    
    // Only process for user's own team's messages
    if (unliftedFlags.length > 0 && String(lastMessage.teamId) === String(selectedTeam)) {
      // Banner notification completely removed per user request
      
      // DISABLED: Never auto-open flag dialogs
      // handleOpenFlagDialog(unliftedFlags[0]);
    }
  }, [currentDebate?.messages, selectedTeam]);

  // Add a function to manually view flags (reserved for future UI)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleViewFlags = useCallback(() => {
    if (!currentDebate?.messages?.length) return;
    
    const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
    const unliftedFlags = lastMessage.flags?.filter(flag => !flag.lifted) || [];
    
    if (unliftedFlags.length > 0) {
      // Manually open the dialog
      handleOpenFlagDialog(unliftedFlags[0]);
    }
  }, [currentDebate?.messages, handleOpenFlagDialog]);

  // Function to check if reformulation was self-initiated
  const isSelfInitiatedReformulation = useMemo((): boolean => {
    // CRITICAL FIX: For better real-time experience, check if the current team's
    // reformulation is self-initiated by looking at our local state first
    
    // Check if the current team is the one that needs to reformulate
    const needsReformulation = currentTeamWithFlag === selectedTeam;
    if (!needsReformulation) return false;
    
    // Priority 1: Check our local state tracking which we know is reliable and up-to-date
    if (selfInitiatedReformulationTeamId === selectedTeam) {
      return true;
    }
    
    // Priority 2: Fall back to server data if we have a reformulation request for this team
    if (currentDebate?.reformulationRequest?.teamId === selectedTeam) {
      const serverSelfInitiated = !!(currentDebate.reformulationRequest as any).selfInitiated;
      return serverSelfInitiated;
    }
    
    // Default: not self-initiated
    return false;
  }, [currentTeamWithFlag, selectedTeam, selfInitiatedReformulationTeamId, currentDebate?.reformulationRequest]);

  // Function to fetch usernames for team members and message senders
  const fetchUsernames = useCallback(async () => {
    if (!currentDebate?.teams) return;
    
    try {
      // Collect all unique user IDs
      const userIds = new Set<string>();
      
      // Add team members
      currentDebate.teams.forEach(team => {
        if (team.userId) userIds.add(String(team.userId));
        if (team.members && Array.isArray(team.members)) {
          team.members.forEach(memberId => {
            if (typeof memberId === 'string') {
              userIds.add(memberId);
            }
          });
        }
      });
      
      // Add message senders and flag lifters
      if (currentDebate.messages && Array.isArray(currentDebate.messages)) {
        currentDebate.messages.forEach(message => {
          if (message.senderId && !message.senderId.startsWith('team_')) {
            userIds.add(String(message.senderId));
          }
          const flags = (message as any).flags;
          if (Array.isArray(flags)) {
            flags.forEach((flag: any) => {
              const lifterId = flag.liftedBy || flag.lifted_by;
              if (lifterId) userIds.add(String(lifterId));
            });
          }
          const noteBy = (message as any).flagsLiftedNote?.byUserId;
          if (noteBy) userIds.add(String(noteBy));
        });
      }
      
      // If no users to fetch, return
      if (userIds.size === 0) return;
      
      // Check if we already have all the usernames we need
      const currentUserIds = Array.from(userIds);
      const cachedUsernames = Object.keys(usernames);
      const missingUserIds = currentUserIds.filter(id => !cachedUsernames.includes(id));
      
      // If we already have all usernames, don't make another API call
      if (missingUserIds.length === 0) {
        logger.info('All usernames already cached, skipping API call');
        return;
      }
      
      logger.info(`Fetching information for ${missingUserIds.length} new users (${userIds.size} total)`);
      
      // Make API request to get user details only for missing users
      const usersData = await getUsersByIds(missingUserIds);
      
      // Create a map of user IDs to usernames, preserving existing data
      const usernamesMap: {[key: string]: string} = { ...usernames };
      Object.keys(usersData).forEach(userId => {
        usernamesMap[userId] = usersData[userId].username;
      });
      
      // Also add current user if available
      if (user?.id && user?.username) {
        usernamesMap[user.id] = user.username;
      }
      
      // Set the usernames
      setUsernames(usernamesMap);
    } catch (error) {
      logger.error('Error fetching usernames:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentDebate.messages/usernames would cause loops
  }, [currentDebate?.id, currentDebate?.teams, currentDebate?.messages?.length, user?.id, user?.username]);
  
  // Call the fetchUsernames function when the debate changes or when we get new messages
  useEffect(() => {
    if (currentDebate?.id) {
      fetchUsernames();
    }
  }, [currentDebate?.id, fetchUsernames]); // Keep minimal dependencies

  // Set team colors for typing indicators
  useEffect(() => {
    if (currentDebate?.teams && currentDebate.teams.length > 0) {
      const colors: {[key: string]: string} = {};
      
      // Set colors for each team
      currentDebate.teams.forEach((team, index) => {
        // Use blue for team 1 and pink for team 2
        colors[team.id] = index === 0 ? '#e3f2fd' : '#fce4ec';
      });
      
      setTeamColors(colors);
    }
  }, [currentDebate?.teams]);

  // Error state - must be after all hook definitions
  if (fetchError && !currentDebate) {
    logger.info('DebateRoomPage: Showing fetch error state');
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage || t('errorLoadingDebateText')}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }
  
  // Add guard condition to make TypeScript happy - ensure currentDebate is defined
  if (!currentDebate) {
    logger.info('DebateRoomPage: currentDebate is null, showing loading state');
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Now TypeScript knows currentDebate is not null in the rest of the component
  const debate = currentDebate; // Create a non-null reference for TypeScript's benefit

  // IMPORTANT: We've added UI logic to allow teams to self-initiate reformulation of their flagged messages.
  // This appears in the UI as an "I Prefer to Reformulate" button when a team has a flagged message.
  // The logic has been implemented with the handleSelfReformulation function.

  // No duplicate emergency fix here - it's already defined at the top of the component
  
  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
      {/* Show loading state if no debate data yet */}
      {(!currentDebate) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('loadingDebateText')}
          </Typography>
        </Box>
      )}

      {/* Show error if fetch failed */}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {errorMessage || 'Failed to load debate. Please try again later.'}
        </Alert>
      )}
      
      {/* Main debate content - only show if debate is loaded */}
      {currentDebate && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1">
              {debate.topic}
            </Typography>
            
            {/* Remove the toggle button for Pusher */}
          </Box>
          
          {/* Display summary for completed debates at the top of the page */}
          {currentDebate.status === 'completed' && (
            <Paper 
              elevation={2} 
          sx={{ 
                mb: 3, 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.light',
                bgcolor: 'background.paper' 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrophyIcon sx={{ mr: 1, color: 'gold' }} />
                <Typography variant="h5" color="primary.main">
                  Debate Results
              </Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {t('thisDebateHasConcludedText')}
              </Typography>
                <Typography variant="body2">
                  {currentDebate.winner && (
                    <>The winner is <strong>{getTeamName(currentDebate.winner, currentDebate)}</strong>.</>
                  )}
                </Typography>
        </Alert>
              
              {/* Include the debate summary directly on the page */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    {currentDebate.cubeChallenge?.isCubeChallenge ? t('challengeAssessment') : t('debateRoomSummaryText')}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => setNotesOpen(v => !v)}>
                    {notesOpen ? t('hideNotes', 'Hide Notes') : t('showNotes', 'Show Notes')}
                  </Button>
                </Box>

                <Collapse in={notesOpen} unmountOnExit>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {t('yourNotes', 'Your Notes (only you can see this)')}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={10}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t('notesPlaceholder', 'Jot down thoughts, TODOs, sources to check later...')}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                      <Button size="small" onClick={() => setNotesOpen(false)}>
                        {t('close', 'Close')}
                      </Button>
                      <Button size="small" variant="contained" onClick={async () => { if(!currentDebate?.id) return; if (notes && notes.length > 50000) { toast.error(t('notesTooLong', 'Notes too long (max 50000 characters)')); return; } setNotesSaving(true); try { await saveUserNotes(currentDebate.id, notes); } catch(e){ logger.error('Failed to save notes:', e); } finally { setNotesSaving(false);} }} disabled={notesSaving}>
                        {notesSaving ? t('saving', 'Saving...') : t('save', 'Save')}
                      </Button>
                    </Box>
                  </Paper>
                </Collapse>
                
                {!currentDebate.summary ? (
                  <Alert severity="info">
                    {currentDebate.cubeChallenge?.isCubeChallenge 
                      ? 'The challenge assessment is being generated. Please check back shortly.'
                      : 'The summary for this debate is being generated. Please check back shortly.'
                    }
                  </Alert>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: currentDebate.cubeChallenge?.isCubeChallenge 
                      ? (currentDebate.summary.includes('CHALLENGE SUCCESSFUL') ? 'success.light' : 'info.light')
                      : 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: currentDebate.cubeChallenge?.isCubeChallenge 
                      ? (currentDebate.summary.includes('CHALLENGE SUCCESSFUL') ? 'success.main' : 'info.main')
                      : 'grey.200',
                    maxHeight: '250px',
                    overflow: 'auto'
                  }}>
                    {currentDebate.summary.split('\n').map((paragraph: string, index: number) => {
                      // Special formatting for CUBE challenge assessment
                      if (currentDebate.cubeChallenge?.isCubeChallenge) {
                        if (paragraph.includes('🎯 CHALLENGE SUCCESSFUL') || paragraph.includes('🛡️ EXPLANATION MAINTAINED')) {
                          return (
                            <Typography key={index} variant="h6" sx={{ 
                              fontWeight: 'bold', 
                              color: paragraph.includes('SUCCESSFUL') ? 'success.dark' : 'info.dark',
                              mb: 2
                            }}>
                              {paragraph}
                            </Typography>
                          );
                        }
                        if (paragraph.startsWith('CHALLENGE ASSESSMENT:') || paragraph.startsWith('FINAL DECISION:')) {
                          return (
                            <Typography key={index} variant="subtitle1" sx={{ 
                              fontWeight: 'bold', 
                              mt: 2, 
                              mb: 1,
                              color: 'primary.dark'
                            }}>
                              {paragraph}
                            </Typography>
                          );
                        }
                      }
                      
                      return (
                        <Typography key={index} variant="body1" paragraph>
                          {paragraph}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => setShowDebateOverDialog(true)}
                >
                  View Full Results
                </Button>
            </Box>
          </Paper>
          )}
          
          {/* Show team information */}
          {renderTeamsBanner()}
          
          {/* Personal Notes above both columns */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {t('yourNotes', 'Your Notes (only you can see this)')}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setNotesOpen(v => !v)}>
                {notesOpen ? t('hideNotes', 'Hide Notes') : t('showNotes', 'Show Notes')}
              </Button>
            </Box>
            <Collapse in={notesOpen} unmountOnExit>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={10}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('notesPlaceholder', 'Jot down thoughts, TODOs, sources to check later...')}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                  <Button size="small" onClick={() => setNotesOpen(false)}>
                    {t('close', 'Close')}
                  </Button>
                  <Button size="small" variant="contained" onClick={async () => { if(!currentDebate?.id) return; if (notes && notes.length > 50000) { toast.error(t('notesTooLong', 'Notes too long (max 50000 characters)')); return; } setNotesSaving(true); try { await saveUserNotes(currentDebate.id, notes); } catch(e){ logger.error('Failed to save notes:', e); } finally { setNotesSaving(false);} }} disabled={notesSaving}>
                    {notesSaving ? t('saving', 'Saving...') : t('save', 'Save')}
                  </Button>
                </Box>
              </Paper>
            </Collapse>
          </Box>

          {/* Main content area with sidebar layout - overflow visible so sticky works */}
          <Grid container spacing={2} sx={{ overflow: 'visible' }}>
            {/* Left sidebar - Debate Overview - column must stretch to match right height so sticky stays in view when scrolling */}
            <Grid item xs={12} md={3} sx={{ mb: { xs: 2, md: 0 } }}>
              <Paper sx={{ 
                p: 2, 
                borderRadius: 2,
                height: 'auto',
                overflowY: 'visible', // No internal scrolling
                position: 'sticky',
                top: 16
              }}>
                <Typography variant="h6" sx={{ mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                  {t('debateOverviewText')}
                </Typography>
                
                <Box>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                    {t('statusText')} <strong style={{ marginLeft: 8 }}>{currentDebate.status === 'active' ? t('inProgressText') : currentDebate.status === 'completed' ? t('completedText') : t('pendingText')}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {currentDebate.isPublic ? (
                      <PublicIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                    ) : (
                      <LockIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                    )}
                    {currentDebate.isPublic ? t('publicText') : t('privateText')}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ChatIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                                          {t('messagesText')} <strong style={{ marginLeft: 8 }}>{currentDebate.messages.length}</strong>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                                          {t('startedText')} <strong style={{ marginLeft: 8 }}>{new Date(debate.createdAt).toLocaleString()}</strong>
                        </Typography>
                  
                  {/* Email notification opt-in moved near concede button */}

                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <UpdateIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                    {t('lastMessageText')} <strong style={{ marginLeft: 8 }}>
                      {debate.messages.length > 0 
                        ? new Date(debate.messages[debate.messages.length - 1].timestamp || debate.updatedAt).toLocaleString() 
                        : t('noMessagesYetText')}
                    </strong>
                  </Typography>
                </Box>
                
                {currentDebate.status === 'completed' && currentDebate.winner && (
                  <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                    Winner: {getTeamName(currentDebate.winner, currentDebate)}
                  </Alert>
                )}
                
                <Typography variant="h6" sx={{ mt: 3, mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    {t('jestersGauntletCountsText')}
                          </Typography>
                
                {currentDebate.teams && currentDebate.teams.map(team => {
                  const teamCounts = shitmoveCounts[team.id] || { total: 0, byType: {} };
                  const isUserTeam = selectedTeam === team.id;
                  
                  // Create color-coded background based on penalty count and team
                  const dangerLevel = teamCounts.total >= 4 ? 'error.light' : 
                                     teamCounts.total >= 3 ? 'warning.light' : 
                                     'background.paper';
                  
                  const baseColor = isUserTeam ? 'primary.lighter' : 'grey.100';
                  const bgColor = teamCounts.total > 0 ? dangerLevel : baseColor;
                    
                    return (
                      <Box 
                      key={team.id} 
                        sx={{
                        mb: 2, 
                        p: 1.5, 
                          borderRadius: 1,
                        backgroundColor: bgColor,
                        border: isUserTeam ? '1px solid #2196f3' : '1px solid #e0e0e0',
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: isUserTeam ? 'primary.main' : 'text.primary',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>{team.name}</span>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={t('teamsLoseTooltipText')}>
                            <InfoIcon fontSize="small" sx={{ mr: 1, color: 'info.main', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        </Typography>
                        
                      {Object.entries(teamCounts.byType).length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {Object.entries(teamCounts.byType).map(([type, count]) => (
                              <Chip
                              key={type}
                              label={`${type.replace(/_/g, ' ')}: ${count}`}
                              color={count >= 2 ? "error" : "default"}
                                size="small"
                              />
                            ))}
                          </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {t('noPenaltiesText')}
                        </Typography>
                        )}
                      </Box>
                    );
                  })}
                
                <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
                  <Typography variant="body2">
                    <strong>{t('aboutPenaltiesText')}</strong> {t('aboutPenaltiesDescText')}
                  </Typography>
                </Alert>
                
                {/* Email opt-in above concede */}
                <Box sx={{ mt: 2, mb: 1, p: 1, bgcolor: '#fafafa', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifyOnOpponentMessage}
                        onChange={(e) => setNotifyOnOpponentMessage(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('notifyOnOpponentMessageText')}
                  />
                </Box>

                {/* Concede and Back buttons in normal document flow */}
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={handleOpenConcedeDialog}
                    sx={{ mb: 2 }}
                  >
                    {t('concedeDebateText')}
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    fullWidth
                    component={RouterLink}
                    to="/"
                    startIcon={<HomeIcon />}
                  >
                    {t('backHomeText')}
                  </Button>
                </Box>
                
              </Paper>
            </Grid>
            
            {/* Main content area - Messages */}
            <Grid item xs={12} md={9}>
              {/* Flag banner completely removed per user request */}
              
              {/* Display debate messages */}
              <Paper sx={{ 
                p: 3, 
                display: 'flex', 
                flexDirection: 'column',
                height: 'auto',
                overflow: 'visible' // Experimental: remove internal scrolling
              }}>
                
                {/* Messages */}
                <Box sx={{ 
                  flexGrow: 1, 
                  overflowY: 'visible', 
                  mb: 2,
                  pr: 1, // Add some padding for the scrollbar
                  minHeight: 0 // Important for nested scrolling in flex layouts
                }}>
                  {currentDebate.messages.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('noMessagesStartDebateText')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {/* Display debate messages */}
                   {currentDebate.messages.map((message, index) => (
                        <React.Fragment key={`message-container-${(message as any).id || (message as any)._id || index}`}>
                          {renderMessage(message)}
                        </React.Fragment>
                      ))}
                      {/* Reference for auto-scrolling */}
                      <div ref={messagesEndRef} />
                    </Box>
                  )}
                </Box>
                
                {/* Input area */}
                <Divider />
                
                {/* Add typing indicator */}
                {pusher?.typingUsers && pusher.typingUsers.length > 0 && (
                  <TypingIndicator 
                    typingUsers={pusher.typingUsers} 
                    teamColors={teamColors}
                    isTypingText={t('isTypingText')}
                    areTypingText={t('areTypingText')}
                    andText={t('andText')}
                    othersText={t('othersText')}
                  />
                )}
                
                {currentDebate.status === 'completed' ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('debateHasConcluded')}
                  </Alert>
                ) : isCurrentTeamTurn() ? (
                  mustHandleFlags ? (
                    // Show prompt to handle flags instead of input box
                    <Alert severity="warning" sx={{ display: 'flex', flexDirection: 'column', p: 2, mt: 2 }}>
                      {flagActionTaken ? (
                        <>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>{t('takenActionOnFlaggedMessage')}</strong>
                          </Typography>
                          <Typography variant="body2">
                            {t('waitingOtherTeamRespond')}
                          </Typography>
                        </>
                      ) : !currentDebate?.messages?.length ? (
                        <Typography variant="body1">
                          <strong>{t('loadingMessages')}</strong>
                        </Typography>
                      ) : (() => {
                        // Get the message that needs handling
                        const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
                        const isMyMessage = String(lastMessage.teamId) === String(selectedTeam);
                        
                        if (isMyMessage) {
                          // My message has flags - I need to reformulate
                            return (
                            <>
                              <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>{t('yourMessageHasBeenFlagged')}</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('waitForOtherTeamDecision')}
                              </Typography>
                            </>
                            );
                          } else {
                          // Other team's message has flags - I need to decide action
                            return (
                            <>
                              <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>{t('actionRequiredText')}</strong> {t('otherTeamMessageFlaggedText')}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('reviewFlagsText')}
                              </Typography>
                            </>
                          );
                        }
                      })()
                      }
                    </Alert>
                  ) : (
                    // Show input box only if it's my turn AND I don't need to handle flags
                    <Box 
                      sx={{ 
                        bgcolor: 'background.paper',
                        pt: 2,
                        mt: 1
                      }}
                    >
                      {/* Add a notification when reformulation has been requested */}
                      {needsToReformulate() && (
                        <Alert 
                          severity={isSelfInitiatedReformulation ? "info" : "warning"} 
                          sx={{ 
                            mb: 2, 
                            bgcolor: isSelfInitiatedReformulation ? '#e3f2fd' : '#fff3e0', 
                            border: isSelfInitiatedReformulation ? '1px solid #2196f3' : '1px solid #ed6c02',
                      display: 'flex', 
                            alignItems: 'center'
                          }}
                        >
                          {isSelfInitiatedReformulation ? (
                            <InfoIcon sx={{ mr: 1, color: '#2196f3' }} />
                          ) : (
                            <WarningIcon sx={{ mr: 1, color: '#ed6c02' }} />
                          )}
                          <Typography variant="body2">
                            <strong>
                              {isSelfInitiatedReformulation 
                                ? t('selfInitiatedReformulationText') 
                                : t('reformulationRequestedText')}
                            </strong>
                            {isSelfInitiatedReformulation
                              ? " You've chosen to reformulate your message. Please address the flagged issues in your response."
                              : " The other team has requested that you reformulate your message. Please address the flagged issues in your response."}
                      </Typography>
                        </Alert>
              )}
            
                <DebateMessageInputForm
                  debateId={currentDebate.id}
                  teamId={selectedTeam || ''}
                  isTeamTurn={isCurrentTeamTurn()}
                  isReformulating={isReformulating}
                  reformulationMessage={reformulationMessage}
                  handleSendMessage={handleSendMessage}
                />
                      {/* Message input handling now moved to DebateMessageInputForm component */}
                    </Box>
                  )
                ) : (
                  <>
                    {/* First check if there are any messages at all */}
                    {!currentDebate.messages || currentDebate.messages.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {t('waitingForFirstTeamText')}
                      </Alert>
                    ) : (() => {
                      // Get the most recent message
                      const lastMessage = currentDebate.messages[currentDebate.messages.length - 1];
                      // Safe access with null checks
                      const hasUnliftedFlags = lastMessage && lastMessage.flags ? 
                        lastMessage.flags.some(flag => !flag.lifted) : false;
                      const isMessageFromMyTeam = lastMessage ? 
                        String(lastMessage.teamId) === String(selectedTeam) : false;
                      const isReformulatedMessage = lastMessage && lastMessage.isReformulation === true;
                      
                      // Special case 1: My team's reformulated message got flagged again
                      if (hasUnliftedFlags && isMessageFromMyTeam && isReformulatedMessage) {
                        logger.info('DebateRoomPage: Reformulated message got flagged again, showing standard flagged message UI');
                        
                        // REMOVED: The forcible reset that was causing endless loops
                        // Instead, treat this exactly like any other flagged message
                        
                        // Use standard flagged message UI - same as first time
                        // Hide self-reformulation in CUBE debates; show helper text instead
                        if (currentDebate?.cubeChallenge?.isCubeChallenge) {
                          return (
                            <Box sx={{ mt: 2 }}>
                              <Alert severity="info">
                                <Typography variant="body2">
                                  AI will respond automatically.
                                </Typography>
                              </Alert>
                            </Box>
                          );
                        }

                        return (
                          <Box sx={{ mt: 2, bgcolor: 'background.paper' }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body1">
                                  <strong>{t('yourMessageFlaggedAgainText')}</strong>
                        </Typography>
                                <Typography variant="body2">
                                  1. {t('waitOtherTeamReviewText')}
                        </Typography>
                                <Typography variant="body2">
                                  2. {t('chooseReformulateAgainText')}
                    </Typography>
                                <Typography variant="body2" color="error.main" sx={{ fontStyle: 'italic', mt: 1 }}>
                                  {t('penaltyFlagsNoteText')}
                </Typography>
              </Box>
                            </Alert>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                                variant="contained"
                                color="warning"
                  onClick={() => {
                                  // Call handleSelfReformulation with the flagged message
                    handleSelfReformulation(lastMessage);
                  }}
                >
                  {t('iPreferToReformulateText')}
                </Button>
                            </Box>
                          </Box>
                        );
                      }
                      
                      // Find the most recent message from the current team
                      const myTeamMessages = currentDebate.messages.filter(m => 
                        m && m.teamId === selectedTeam
                      );
                      
                      // Special case 2: Regular flagged message that needs reformulation
                      if (!reformulationRequested && myTeamMessages.length > 0) {
                        // Get the most recent message from my team
                        const lastMessageFromMyTeam = myTeamMessages[myTeamMessages.length - 1];
                        
                        // Check if this message has unlifted flags
                        const hasUnliftedFlags = lastMessageFromMyTeam.flags?.some(flag => !flag.lifted) || false;
                        const noReformulationYet = currentTeamWithFlag !== lastMessageFromMyTeam.teamId;
                        
                        // Only show the reformulation option for the most recent message from my team if it has flags
                        if (hasUnliftedFlags && noReformulationYet) {
                if (currentDebate?.cubeChallenge?.isCubeChallenge) {
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info">
                        <Typography variant="body2">
                          AI will respond automatically.
                        </Typography>
                      </Alert>
                    </Box>
                  );
                }
                return (
                            <Box sx={{ mt: 2, bgcolor: 'background.paper' }}>
                              {reformulationRequested ? (
                                <Alert severity="info">
                                  {t('reformulationRequestedWaitText')}
                                </Alert>
                              ) : (
                                <>
                                  <Alert severity="info" sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      <Typography variant="body1">
                                        {t('yourRecentMessageFlaggedText')}
                                      </Typography>
                                      <Typography variant="body2">
                                        1. {t('waitOtherTeamReviewText')}
                                      </Typography>
                                                                      <Typography variant="body2">
                                  2. {t('chooseReformulateNowText')}
                                      </Typography>
                                                                              <Typography variant="body2" color="error.main" sx={{ fontStyle: 'italic', mt: 1 }}>
                                          {t('penaltyFlagsNoteText')}
                                      </Typography>
                                    </Box>
                                  </Alert>
                                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Button 
                                      variant="contained"
                                      color="warning"
                        onClick={() => {
                                        // Use the most recent message from my team that has flags
                                        handleSelfReformulation(lastMessageFromMyTeam);
                                      }}
                                    >
                                      {t('iPreferToReformulateText')}
                      </Button>
                                  </Box>
                                </>
                              )}
                            </Box>
                          );
                        }
                      }
                      
                      // Case 3: Waiting for reformulation
                      // Only show this if:
                      // 1. Current team has flags (currentTeamWithFlag === selectedTeam)
                      // 2. The most recent message is from this team (currentDebate.messages.length > 0 && currentDebate.messages[currentDebate.messages.length - 1].teamId === selectedTeam)
                      // 3. The most recent message has unlifted flags
                      if (currentDebate.messages.length > 0 && 
                          currentTeamWithFlag === selectedTeam &&
                          currentDebate.messages[currentDebate.messages.length - 1].teamId === selectedTeam &&
                          currentDebate.messages[currentDebate.messages.length - 1].flags &&
                          currentDebate.messages[currentDebate.messages.length - 1].flags.some(flag => !flag.lifted)) {
                        return (
                          <Alert severity="warning" sx={{ p: 2, mt: 2 }}>
                            <Typography variant="body1">
                              <strong>{t('yourMessageFlaggedNeedsReformulationText')}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {t('submitImprovedMessageText')}
                    </Typography>
                  </Alert>
                );
              }
                      
                      // Case 4: Any other case (waiting for the other team)
                      return (
                        <Alert severity={hasRequestedReformulationValue ? "warning" : "info"} sx={{ mt: 2 }}>
                          {hasRequestedReformulationValue ? (
                            <>
                              <Typography variant="body1">
                                <strong>
                                  {isSelfInitiatedReformulation ? 
                                    "The other team has chosen to reformulate their message." : 
                                    "You've requested reformulation from the other team."}
                                </strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {t('waitingThemSubmitImprovedText')}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body1">
                                {currentDebate?.cubeChallenge?.isCubeChallenge ? 
                                  (currentDebate.cubeChallenge.aiResponseInProgress ? 
                                    '🤖 AI defender is analyzing and crafting response...' : 
                                    t('waitingCubeAIResponseText')
                                  ) : 
                                  t('waitingOtherTeamSendText')}
                              </Typography>
                              {currentDebate?.cubeChallenge?.isCubeChallenge && currentDebate.cubeChallenge.aiResponseInProgress && (
                                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                                  This may take several minutes. The page will update automatically when complete.
                                </Typography>
                              )}
                            </>
                          )}
                        </Alert>
                      );
                    })()}
                  </>
                )}
                
                {/* Permanent AI Defense Failure Notification */}
                {aiDefenseFailed && currentDebate?.cubeChallenge?.isCubeChallenge && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 2, 
                      p: 2,
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                      '& .MuiAlert-icon': {
                        color: 'error.contrastText'
                      }
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t('cubeAIDefenseFailedText')}
                    </Typography>
                    <Typography variant="body2">
                      {aiFailureMessage || t('cubeAIDefenseFailedPermanent')}
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
      
      {/* Flag Dialog */}
          <Dialog
        open={flagDialogOpen}
        onClose={handleCloseFlagDialog}
        aria-labelledby="flag-dialog-title"
            maxWidth="sm"
            fullWidth
          >
        <DialogTitle id="flag-dialog-title">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('messageFlagsText')}</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseFlagDialog}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
            </DialogTitle>
        <DialogContent>
          {selectedFlag && (
                  <>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'error.main' }}>
                {t('flagTypeText')} {selectedFlag.type}
                    </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedFlag.explanation}
                    </Typography>
              
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('optionsText')}
                  </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    // Self-reformulation: show input with guidance
                    setIsReformulating(true);
                    setReformulationRequested(true);
                    handleCloseFlagDialog();
                  }}
                  startIcon={<EditIcon />}
                >
                  {t('reformulateYourMessageText')}
                </Button>
                          </Box>
            </>
          )}
            </DialogContent>
        <DialogActions>
              <Button
            onClick={handleCloseFlagDialog} 
            color="primary"
              >
            {t('closeText')}
              </Button>
            </DialogActions>
          </Dialog>

      {/* Concede Dialog */}
          <Dialog
            open={concedeDialogOpen}
            onClose={handleCloseConcedeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FlagIcon color="error" sx={{ mr: 1 }} />
            {t('concedeDefeatText')}
              </Box>
            </DialogTitle>
            <DialogContent>
          <DialogContentText>
            {t('areYouSureConcedeText')}
          </DialogContentText>
            </DialogContent>
            <DialogActions>
          <Button onClick={handleCloseConcedeDialog}>
            {t('noText')}
              </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConcede}
          >
            {t('concedeDefeatText')}
              </Button>
            </DialogActions>
          </Dialog>

      {/* Debate Over Dialog */}
      <Dialog
        open={showDebateOverDialog}
        onClose={() => setShowDebateOverDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TrophyIcon sx={{ mr: 1, color: 'gold' }} />
            {t('debateRoomResultsText')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DebateSummary
            debate={currentDebate}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              setShowDebateOverDialog(false);
              navigate('/');
            }}
          >
            {t('returnToHomeText')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Debate Result Dialog - Modified */}
          <Dialog
            open={debateResultDialogOpen}
            onClose={() => setDebateResultDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
        <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrophyIcon sx={{ mr: 1, color: 'gold' }} />
            {t('debateCompletedText')}
              </Box>
            </DialogTitle>
            <DialogContent>
          <DialogContentText>
            {t('thisDebateHasConcludedText')}
            {currentDebate?.winner && (
              <>
                <br />
                The winner is <strong>{getTeamName(currentDebate.winner, currentDebate)}</strong>.
                  </>
                )}
            <br /><br />
            {currentDebate?.summary
              ? t('summaryGeneratedText')
              : (currentDebate?.cubeChallenge?.isCubeChallenge
                  ? 'The challenge assessment is being generated. Please check back shortly.'
                  : 'The summary for this debate is being generated. Please check back shortly.')}
          </DialogContentText>
            </DialogContent>
            <DialogActions>
          <Button 
            variant="contained" 
            onClick={() => setDebateResultDialogOpen(false)}
          >
            {t('viewSummaryText')}
              </Button>
        </DialogActions>
      </Dialog>
      
      {/* Use Pusher in the background by rendering the component but make it invisible */}
      {usePusher && currentDebate && selectedTeam && (
        <div style={{ display: 'none' }}>
          <PusherDebateChat 
            debateId={currentDebate.id} 
            teamId={selectedTeam} 
          />
        </div>
      )}
      
      {/* Connection status indicator */}
      {usePusher && (
        <div style={{ position: 'fixed', bottom: 10, right: 10, zIndex: 1000 }}>
          <Box 
            sx={{ 
              bgcolor: isPusherConnected ? 'success.main' : 'error.main',
              color: 'white',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'inline-block',
              boxShadow: 2
            }} 
          />
        </div>
      )}
    </Container>
  );
};

export default DebateRoomPage; 